import { ApiError, AuthArmorClient } from '@autharmor/autharmor-js';

const equalFn = (a, b) => a === b;
const $PROXY = Symbol("solid-proxy");
const $TRACK = Symbol("solid-track");
const signalOptions = {
  equals: equalFn
};
let runEffects = runQueue;
const STALE = 1;
const PENDING = 2;
const UNOWNED = {
  owned: null,
  cleanups: null,
  context: null,
  owner: null
};
var Owner = null;
let Transition = null;
let Listener = null;
let Updates = null;
let Effects = null;
let ExecCount = 0;
function createRoot(fn, detachedOwner) {
  const listener = Listener,
    owner = Owner,
    unowned = fn.length === 0,
    current = detachedOwner === undefined ? owner : detachedOwner,
    root = unowned ? UNOWNED : {
      owned: null,
      cleanups: null,
      context: current ? current.context : null,
      owner: current
    },
    updateFn = unowned ? fn : () => fn(() => untrack(() => cleanNode(root)));
  Owner = root;
  Listener = null;
  try {
    return runUpdates(updateFn, true);
  } finally {
    Listener = listener;
    Owner = owner;
  }
}
function createSignal(value, options) {
  options = options ? Object.assign({}, signalOptions, options) : signalOptions;
  const s = {
    value,
    observers: null,
    observerSlots: null,
    comparator: options.equals || undefined
  };
  const setter = value => {
    if (typeof value === "function") {
      value = value(s.value);
    }
    return writeSignal(s, value);
  };
  return [readSignal.bind(s), setter];
}
function createRenderEffect(fn, value, options) {
  const c = createComputation(fn, value, false, STALE);
  updateComputation(c);
}
function createEffect(fn, value, options) {
  runEffects = runUserEffects;
  const c = createComputation(fn, value, false, STALE);
  if (!options || !options.render) c.user = true;
  Effects ? Effects.push(c) : updateComputation(c);
}
function createMemo(fn, value, options) {
  options = options ? Object.assign({}, signalOptions, options) : signalOptions;
  const c = createComputation(fn, value, true, 0);
  c.observers = null;
  c.observerSlots = null;
  c.comparator = options.equals || undefined;
  updateComputation(c);
  return readSignal.bind(c);
}
function untrack(fn) {
  if (Listener === null) return fn();
  const listener = Listener;
  Listener = null;
  try {
    return fn();
  } finally {
    Listener = listener;
  }
}
function on(deps, fn, options) {
  const isArray = Array.isArray(deps);
  let prevInput;
  let defer = options && options.defer;
  return prevValue => {
    let input;
    if (isArray) {
      input = Array(deps.length);
      for (let i = 0; i < deps.length; i++) input[i] = deps[i]();
    } else input = deps();
    if (defer) {
      defer = false;
      return undefined;
    }
    const result = untrack(() => fn(input, prevInput, prevValue));
    prevInput = input;
    return result;
  };
}
function onCleanup(fn) {
  if (Owner === null) ;else if (Owner.cleanups === null) Owner.cleanups = [fn];else Owner.cleanups.push(fn);
  return fn;
}
function createContext(defaultValue, options) {
  const id = Symbol("context");
  return {
    id,
    Provider: createProvider(id),
    defaultValue
  };
}
function useContext(context) {
  return Owner && Owner.context && Owner.context[context.id] !== undefined ? Owner.context[context.id] : context.defaultValue;
}
function children(fn) {
  const children = createMemo(fn);
  const memo = createMemo(() => resolveChildren(children()));
  memo.toArray = () => {
    const c = memo();
    return Array.isArray(c) ? c : c != null ? [c] : [];
  };
  return memo;
}
function readSignal() {
  if (this.sources && (this.state)) {
    if ((this.state) === STALE) updateComputation(this);else {
      const updates = Updates;
      Updates = null;
      runUpdates(() => lookUpstream(this), false);
      Updates = updates;
    }
  }
  if (Listener) {
    const sSlot = this.observers ? this.observers.length : 0;
    if (!Listener.sources) {
      Listener.sources = [this];
      Listener.sourceSlots = [sSlot];
    } else {
      Listener.sources.push(this);
      Listener.sourceSlots.push(sSlot);
    }
    if (!this.observers) {
      this.observers = [Listener];
      this.observerSlots = [Listener.sources.length - 1];
    } else {
      this.observers.push(Listener);
      this.observerSlots.push(Listener.sources.length - 1);
    }
  }
  return this.value;
}
function writeSignal(node, value, isComp) {
  let current = node.value;
  if (!node.comparator || !node.comparator(current, value)) {
    node.value = value;
    if (node.observers && node.observers.length) {
      runUpdates(() => {
        for (let i = 0; i < node.observers.length; i += 1) {
          const o = node.observers[i];
          const TransitionRunning = Transition && Transition.running;
          if (TransitionRunning && Transition.disposed.has(o)) ;
          if (TransitionRunning ? !o.tState : !o.state) {
            if (o.pure) Updates.push(o);else Effects.push(o);
            if (o.observers) markDownstream(o);
          }
          if (!TransitionRunning) o.state = STALE;
        }
        if (Updates.length > 10e5) {
          Updates = [];
          if (false) ;
          throw new Error();
        }
      }, false);
    }
  }
  return value;
}
function updateComputation(node) {
  if (!node.fn) return;
  cleanNode(node);
  const owner = Owner,
    listener = Listener,
    time = ExecCount;
  Listener = Owner = node;
  runComputation(node, node.value, time);
  Listener = listener;
  Owner = owner;
}
function runComputation(node, value, time) {
  let nextValue;
  try {
    nextValue = node.fn(value);
  } catch (err) {
    if (node.pure) {
      {
        node.state = STALE;
        node.owned && node.owned.forEach(cleanNode);
        node.owned = null;
      }
    }
    node.updatedAt = time + 1;
    return handleError(err);
  }
  if (!node.updatedAt || node.updatedAt <= time) {
    if (node.updatedAt != null && "observers" in node) {
      writeSignal(node, nextValue);
    } else node.value = nextValue;
    node.updatedAt = time;
  }
}
function createComputation(fn, init, pure, state = STALE, options) {
  const c = {
    fn,
    state: state,
    updatedAt: null,
    owned: null,
    sources: null,
    sourceSlots: null,
    cleanups: null,
    value: init,
    owner: Owner,
    context: Owner ? Owner.context : null,
    pure
  };
  if (Owner === null) ;else if (Owner !== UNOWNED) {
    {
      if (!Owner.owned) Owner.owned = [c];else Owner.owned.push(c);
    }
  }
  return c;
}
function runTop(node) {
  if ((node.state) === 0) return;
  if ((node.state) === PENDING) return lookUpstream(node);
  if (node.suspense && untrack(node.suspense.inFallback)) return node.suspense.effects.push(node);
  const ancestors = [node];
  while ((node = node.owner) && (!node.updatedAt || node.updatedAt < ExecCount)) {
    if (node.state) ancestors.push(node);
  }
  for (let i = ancestors.length - 1; i >= 0; i--) {
    node = ancestors[i];
    if ((node.state) === STALE) {
      updateComputation(node);
    } else if ((node.state) === PENDING) {
      const updates = Updates;
      Updates = null;
      runUpdates(() => lookUpstream(node, ancestors[0]), false);
      Updates = updates;
    }
  }
}
function runUpdates(fn, init) {
  if (Updates) return fn();
  let wait = false;
  if (!init) Updates = [];
  if (Effects) wait = true;else Effects = [];
  ExecCount++;
  try {
    const res = fn();
    completeUpdates(wait);
    return res;
  } catch (err) {
    if (!wait) Effects = null;
    Updates = null;
    handleError(err);
  }
}
function completeUpdates(wait) {
  if (Updates) {
    runQueue(Updates);
    Updates = null;
  }
  if (wait) return;
  const e = Effects;
  Effects = null;
  if (e.length) runUpdates(() => runEffects(e), false);
}
function runQueue(queue) {
  for (let i = 0; i < queue.length; i++) runTop(queue[i]);
}
function runUserEffects(queue) {
  let i,
    userLength = 0;
  for (i = 0; i < queue.length; i++) {
    const e = queue[i];
    if (!e.user) runTop(e);else queue[userLength++] = e;
  }
  for (i = 0; i < userLength; i++) runTop(queue[i]);
}
function lookUpstream(node, ignore) {
  node.state = 0;
  for (let i = 0; i < node.sources.length; i += 1) {
    const source = node.sources[i];
    if (source.sources) {
      const state = source.state;
      if (state === STALE) {
        if (source !== ignore && (!source.updatedAt || source.updatedAt < ExecCount)) runTop(source);
      } else if (state === PENDING) lookUpstream(source, ignore);
    }
  }
}
function markDownstream(node) {
  for (let i = 0; i < node.observers.length; i += 1) {
    const o = node.observers[i];
    if (!o.state) {
      o.state = PENDING;
      if (o.pure) Updates.push(o);else Effects.push(o);
      o.observers && markDownstream(o);
    }
  }
}
function cleanNode(node) {
  let i;
  if (node.sources) {
    while (node.sources.length) {
      const source = node.sources.pop(),
        index = node.sourceSlots.pop(),
        obs = source.observers;
      if (obs && obs.length) {
        const n = obs.pop(),
          s = source.observerSlots.pop();
        if (index < obs.length) {
          n.sourceSlots[s] = index;
          obs[index] = n;
          source.observerSlots[index] = s;
        }
      }
    }
  }
  if (node.owned) {
    for (i = node.owned.length - 1; i >= 0; i--) cleanNode(node.owned[i]);
    node.owned = null;
  }
  if (node.cleanups) {
    for (i = node.cleanups.length - 1; i >= 0; i--) node.cleanups[i]();
    node.cleanups = null;
  }
  node.state = 0;
}
function castError(err) {
  if (err instanceof Error) return err;
  return new Error(typeof err === "string" ? err : "Unknown error", {
    cause: err
  });
}
function handleError(err, owner = Owner) {
  const error = castError(err);
  throw error;
}
function resolveChildren(children) {
  if (typeof children === "function" && !children.length) return resolveChildren(children());
  if (Array.isArray(children)) {
    const results = [];
    for (let i = 0; i < children.length; i++) {
      const result = resolveChildren(children[i]);
      Array.isArray(result) ? results.push.apply(results, result) : results.push(result);
    }
    return results;
  }
  return children;
}
function createProvider(id, options) {
  return function provider(props) {
    let res;
    createRenderEffect(() => res = untrack(() => {
      Owner.context = {
        ...Owner.context,
        [id]: props.value
      };
      return children(() => props.children);
    }), undefined);
    return res;
  };
}
const FALLBACK = Symbol("fallback");
function dispose(d) {
  for (let i = 0; i < d.length; i++) d[i]();
}
function mapArray(list, mapFn, options = {}) {
  let items = [],
    mapped = [],
    disposers = [],
    len = 0,
    indexes = mapFn.length > 1 ? [] : null;
  onCleanup(() => dispose(disposers));
  return () => {
    let newItems = list() || [],
      i,
      j;
    newItems[$TRACK];
    return untrack(() => {
      let newLen = newItems.length,
        newIndices,
        newIndicesNext,
        temp,
        tempdisposers,
        tempIndexes,
        start,
        end,
        newEnd,
        item;
      if (newLen === 0) {
        if (len !== 0) {
          dispose(disposers);
          disposers = [];
          items = [];
          mapped = [];
          len = 0;
          indexes && (indexes = []);
        }
        if (options.fallback) {
          items = [FALLBACK];
          mapped[0] = createRoot(disposer => {
            disposers[0] = disposer;
            return options.fallback();
          });
          len = 1;
        }
      } else if (len === 0) {
        mapped = new Array(newLen);
        for (j = 0; j < newLen; j++) {
          items[j] = newItems[j];
          mapped[j] = createRoot(mapper);
        }
        len = newLen;
      } else {
        temp = new Array(newLen);
        tempdisposers = new Array(newLen);
        indexes && (tempIndexes = new Array(newLen));
        for (start = 0, end = Math.min(len, newLen); start < end && items[start] === newItems[start]; start++);
        for (end = len - 1, newEnd = newLen - 1; end >= start && newEnd >= start && items[end] === newItems[newEnd]; end--, newEnd--) {
          temp[newEnd] = mapped[end];
          tempdisposers[newEnd] = disposers[end];
          indexes && (tempIndexes[newEnd] = indexes[end]);
        }
        newIndices = new Map();
        newIndicesNext = new Array(newEnd + 1);
        for (j = newEnd; j >= start; j--) {
          item = newItems[j];
          i = newIndices.get(item);
          newIndicesNext[j] = i === undefined ? -1 : i;
          newIndices.set(item, j);
        }
        for (i = start; i <= end; i++) {
          item = items[i];
          j = newIndices.get(item);
          if (j !== undefined && j !== -1) {
            temp[j] = mapped[i];
            tempdisposers[j] = disposers[i];
            indexes && (tempIndexes[j] = indexes[i]);
            j = newIndicesNext[j];
            newIndices.set(item, j);
          } else disposers[i]();
        }
        for (j = start; j < newLen; j++) {
          if (j in temp) {
            mapped[j] = temp[j];
            disposers[j] = tempdisposers[j];
            if (indexes) {
              indexes[j] = tempIndexes[j];
              indexes[j](j);
            }
          } else mapped[j] = createRoot(mapper);
        }
        mapped = mapped.slice(0, len = newLen);
        items = newItems.slice(0);
      }
      return mapped;
    });
    function mapper(disposer) {
      disposers[j] = disposer;
      if (indexes) {
        const [s, set] = createSignal(j);
        indexes[j] = set;
        return mapFn(newItems[j], s);
      }
      return mapFn(newItems[j]);
    }
  };
}
function createComponent(Comp, props) {
  return untrack(() => Comp(props || {}));
}
function trueFn() {
  return true;
}
const propTraps = {
  get(_, property, receiver) {
    if (property === $PROXY) return receiver;
    return _.get(property);
  },
  has(_, property) {
    if (property === $PROXY) return true;
    return _.has(property);
  },
  set: trueFn,
  deleteProperty: trueFn,
  getOwnPropertyDescriptor(_, property) {
    return {
      configurable: true,
      enumerable: true,
      get() {
        return _.get(property);
      },
      set: trueFn,
      deleteProperty: trueFn
    };
  },
  ownKeys(_) {
    return _.keys();
  }
};
function resolveSource(s) {
  return !(s = typeof s === "function" ? s() : s) ? {} : s;
}
function resolveSources() {
  for (let i = 0, length = this.length; i < length; ++i) {
    const v = this[i]();
    if (v !== undefined) return v;
  }
}
function mergeProps(...sources) {
  let proxy = false;
  for (let i = 0; i < sources.length; i++) {
    const s = sources[i];
    proxy = proxy || !!s && $PROXY in s;
    sources[i] = typeof s === "function" ? (proxy = true, createMemo(s)) : s;
  }
  if (proxy) {
    return new Proxy({
      get(property) {
        for (let i = sources.length - 1; i >= 0; i--) {
          const v = resolveSource(sources[i])[property];
          if (v !== undefined) return v;
        }
      },
      has(property) {
        for (let i = sources.length - 1; i >= 0; i--) {
          if (property in resolveSource(sources[i])) return true;
        }
        return false;
      },
      keys() {
        const keys = [];
        for (let i = 0; i < sources.length; i++) keys.push(...Object.keys(resolveSource(sources[i])));
        return [...new Set(keys)];
      }
    }, propTraps);
  }
  const target = {};
  const sourcesMap = {};
  const defined = new Set();
  for (let i = sources.length - 1; i >= 0; i--) {
    const source = sources[i];
    if (!source) continue;
    const sourceKeys = Object.getOwnPropertyNames(source);
    for (let i = 0, length = sourceKeys.length; i < length; i++) {
      const key = sourceKeys[i];
      if (key === "__proto__" || key === "constructor") continue;
      const desc = Object.getOwnPropertyDescriptor(source, key);
      if (!defined.has(key)) {
        if (desc.get) {
          defined.add(key);
          Object.defineProperty(target, key, {
            enumerable: true,
            configurable: true,
            get: resolveSources.bind(sourcesMap[key] = [desc.get.bind(source)])
          });
        } else {
          if (desc.value !== undefined) defined.add(key);
          target[key] = desc.value;
        }
      } else {
        const sources = sourcesMap[key];
        if (sources) {
          if (desc.get) {
            sources.push(desc.get.bind(source));
          } else if (desc.value !== undefined) {
            sources.push(() => desc.value);
          }
        } else if (target[key] === undefined) target[key] = desc.value;
      }
    }
  }
  return target;
}
function splitProps(props, ...keys) {
  if ($PROXY in props) {
    const blocked = new Set(keys.length > 1 ? keys.flat() : keys[0]);
    const res = keys.map(k => {
      return new Proxy({
        get(property) {
          return k.includes(property) ? props[property] : undefined;
        },
        has(property) {
          return k.includes(property) && property in props;
        },
        keys() {
          return k.filter(property => property in props);
        }
      }, propTraps);
    });
    res.push(new Proxy({
      get(property) {
        return blocked.has(property) ? undefined : props[property];
      },
      has(property) {
        return blocked.has(property) ? false : property in props;
      },
      keys() {
        return Object.keys(props).filter(k => !blocked.has(k));
      }
    }, propTraps));
    return res;
  }
  const otherObject = {};
  const objects = keys.map(() => ({}));
  for (const propName of Object.getOwnPropertyNames(props)) {
    const desc = Object.getOwnPropertyDescriptor(props, propName);
    const isDefaultDesc = !desc.get && !desc.set && desc.enumerable && desc.writable && desc.configurable;
    let blocked = false;
    let objectIndex = 0;
    for (const k of keys) {
      if (k.includes(propName)) {
        blocked = true;
        isDefaultDesc ? objects[objectIndex][propName] = desc.value : Object.defineProperty(objects[objectIndex], propName, desc);
      }
      ++objectIndex;
    }
    if (!blocked) {
      isDefaultDesc ? otherObject[propName] = desc.value : Object.defineProperty(otherObject, propName, desc);
    }
  }
  return [...objects, otherObject];
}
const narrowedError = name => `Stale read from <${name}>.`;
function For(props) {
  const fallback = "fallback" in props && {
    fallback: () => props.fallback
  };
  return createMemo(mapArray(() => props.each, props.children, fallback || undefined));
}
function Show(props) {
  const keyed = props.keyed;
  const condition = createMemo(() => props.when, undefined, {
    equals: (a, b) => keyed ? a === b : !a === !b
  });
  return createMemo(() => {
    const c = condition();
    if (c) {
      const child = props.children;
      const fn = typeof child === "function" && child.length > 0;
      return fn ? untrack(() => child(keyed ? c : () => {
        if (!untrack(condition)) throw narrowedError("Show");
        return props.when;
      })) : child;
    }
    return props.fallback;
  }, undefined, undefined);
}
function Switch(props) {
  let keyed = false;
  const equals = (a, b) => a[0] === b[0] && (keyed ? a[1] === b[1] : !a[1] === !b[1]) && a[2] === b[2];
  const conditions = children(() => props.children),
    evalConditions = createMemo(() => {
      let conds = conditions();
      if (!Array.isArray(conds)) conds = [conds];
      for (let i = 0; i < conds.length; i++) {
        const c = conds[i].when;
        if (c) {
          keyed = !!conds[i].keyed;
          return [i, c, conds[i]];
        }
      }
      return [-1];
    }, undefined, {
      equals
    });
  return createMemo(() => {
    const [index, when, cond] = evalConditions();
    if (index < 0) return props.fallback;
    const c = cond.children;
    const fn = typeof c === "function" && c.length > 0;
    return fn ? untrack(() => c(keyed ? when : () => {
      if (untrack(evalConditions)[0] !== index) throw narrowedError("Match");
      return cond.when;
    })) : c;
  }, undefined, undefined);
}
function Match(props) {
  return props;
}

const booleans = ["allowfullscreen", "async", "autofocus", "autoplay", "checked", "controls", "default", "disabled", "formnovalidate", "hidden", "indeterminate", "ismap", "loop", "multiple", "muted", "nomodule", "novalidate", "open", "playsinline", "readonly", "required", "reversed", "seamless", "selected"];
const Properties = /*#__PURE__*/new Set(["className", "value", "readOnly", "formNoValidate", "isMap", "noModule", "playsInline", ...booleans]);
const ChildProperties = /*#__PURE__*/new Set(["innerHTML", "textContent", "innerText", "children"]);
const Aliases = /*#__PURE__*/Object.assign(Object.create(null), {
  className: "class",
  htmlFor: "for"
});
const PropAliases = /*#__PURE__*/Object.assign(Object.create(null), {
  class: "className",
  formnovalidate: {
    $: "formNoValidate",
    BUTTON: 1,
    INPUT: 1
  },
  ismap: {
    $: "isMap",
    IMG: 1
  },
  nomodule: {
    $: "noModule",
    SCRIPT: 1
  },
  playsinline: {
    $: "playsInline",
    VIDEO: 1
  },
  readonly: {
    $: "readOnly",
    INPUT: 1,
    TEXTAREA: 1
  }
});
function getPropAlias(prop, tagName) {
  const a = PropAliases[prop];
  return typeof a === "object" ? a[tagName] ? a["$"] : undefined : a;
}
const DelegatedEvents = /*#__PURE__*/new Set(["beforeinput", "click", "dblclick", "contextmenu", "focusin", "focusout", "input", "keydown", "keyup", "mousedown", "mousemove", "mouseout", "mouseover", "mouseup", "pointerdown", "pointermove", "pointerout", "pointerover", "pointerup", "touchend", "touchmove", "touchstart"]);
const SVGNamespace = {
  xlink: "http://www.w3.org/1999/xlink",
  xml: "http://www.w3.org/XML/1998/namespace"
};
function reconcileArrays(parentNode, a, b) {
  let bLength = b.length,
    aEnd = a.length,
    bEnd = bLength,
    aStart = 0,
    bStart = 0,
    after = a[aEnd - 1].nextSibling,
    map = null;
  while (aStart < aEnd || bStart < bEnd) {
    if (a[aStart] === b[bStart]) {
      aStart++;
      bStart++;
      continue;
    }
    while (a[aEnd - 1] === b[bEnd - 1]) {
      aEnd--;
      bEnd--;
    }
    if (aEnd === aStart) {
      const node = bEnd < bLength ? bStart ? b[bStart - 1].nextSibling : b[bEnd - bStart] : after;
      while (bStart < bEnd) parentNode.insertBefore(b[bStart++], node);
    } else if (bEnd === bStart) {
      while (aStart < aEnd) {
        if (!map || !map.has(a[aStart])) a[aStart].remove();
        aStart++;
      }
    } else if (a[aStart] === b[bEnd - 1] && b[bStart] === a[aEnd - 1]) {
      const node = a[--aEnd].nextSibling;
      parentNode.insertBefore(b[bStart++], a[aStart++].nextSibling);
      parentNode.insertBefore(b[--bEnd], node);
      a[aEnd] = b[bEnd];
    } else {
      if (!map) {
        map = new Map();
        let i = bStart;
        while (i < bEnd) map.set(b[i], i++);
      }
      const index = map.get(a[aStart]);
      if (index != null) {
        if (bStart < index && index < bEnd) {
          let i = aStart,
            sequence = 1,
            t;
          while (++i < aEnd && i < bEnd) {
            if ((t = map.get(a[i])) == null || t !== index + sequence) break;
            sequence++;
          }
          if (sequence > index - bStart) {
            const node = a[aStart];
            while (bStart < index) parentNode.insertBefore(b[bStart++], node);
          } else parentNode.replaceChild(b[bStart++], a[aStart++]);
        } else aStart++;
      } else a[aStart++].remove();
    }
  }
}
const $$EVENTS = "_$DX_DELEGATE";
function template(html, isCE, isSVG) {
  let node;
  const create = () => {
    const t = document.createElement("template");
    t.innerHTML = html;
    return isSVG ? t.content.firstChild.firstChild : t.content.firstChild;
  };
  const fn = isCE ? () => untrack(() => document.importNode(node || (node = create()), true)) : () => (node || (node = create())).cloneNode(true);
  fn.cloneNode = fn;
  return fn;
}
function delegateEvents(eventNames, document = window.document) {
  const e = document[$$EVENTS] || (document[$$EVENTS] = new Set());
  for (let i = 0, l = eventNames.length; i < l; i++) {
    const name = eventNames[i];
    if (!e.has(name)) {
      e.add(name);
      document.addEventListener(name, eventHandler);
    }
  }
}
function setAttribute(node, name, value) {
  if (value == null) node.removeAttribute(name);else node.setAttribute(name, value);
}
function setAttributeNS(node, namespace, name, value) {
  if (value == null) node.removeAttributeNS(namespace, name);else node.setAttributeNS(namespace, name, value);
}
function className(node, value) {
  if (value == null) node.removeAttribute("class");else node.className = value;
}
function addEventListener(node, name, handler, delegate) {
  if (delegate) {
    if (Array.isArray(handler)) {
      node[`$$${name}`] = handler[0];
      node[`$$${name}Data`] = handler[1];
    } else node[`$$${name}`] = handler;
  } else if (Array.isArray(handler)) {
    const handlerFn = handler[0];
    node.addEventListener(name, handler[0] = e => handlerFn.call(node, handler[1], e));
  } else node.addEventListener(name, handler);
}
function classList(node, value, prev = {}) {
  const classKeys = Object.keys(value || {}),
    prevKeys = Object.keys(prev);
  let i, len;
  for (i = 0, len = prevKeys.length; i < len; i++) {
    const key = prevKeys[i];
    if (!key || key === "undefined" || value[key]) continue;
    toggleClassKey(node, key, false);
    delete prev[key];
  }
  for (i = 0, len = classKeys.length; i < len; i++) {
    const key = classKeys[i],
      classValue = !!value[key];
    if (!key || key === "undefined" || prev[key] === classValue || !classValue) continue;
    toggleClassKey(node, key, true);
    prev[key] = classValue;
  }
  return prev;
}
function style(node, value, prev) {
  if (!value) return prev ? setAttribute(node, "style") : value;
  const nodeStyle = node.style;
  if (typeof value === "string") return nodeStyle.cssText = value;
  typeof prev === "string" && (nodeStyle.cssText = prev = undefined);
  prev || (prev = {});
  value || (value = {});
  let v, s;
  for (s in prev) {
    value[s] == null && nodeStyle.removeProperty(s);
    delete prev[s];
  }
  for (s in value) {
    v = value[s];
    if (v !== prev[s]) {
      nodeStyle.setProperty(s, v);
      prev[s] = v;
    }
  }
  return prev;
}
function spread(node, props = {}, isSVG, skipChildren) {
  const prevProps = {};
  if (!skipChildren) {
    createRenderEffect(() => prevProps.children = insertExpression(node, props.children, prevProps.children));
  }
  createRenderEffect(() => props.ref && props.ref(node));
  createRenderEffect(() => assign(node, props, isSVG, true, prevProps, true));
  return prevProps;
}
function use(fn, element, arg) {
  return untrack(() => fn(element, arg));
}
function insert(parent, accessor, marker, initial) {
  if (marker !== undefined && !initial) initial = [];
  if (typeof accessor !== "function") return insertExpression(parent, accessor, initial, marker);
  createRenderEffect(current => insertExpression(parent, accessor(), current, marker), initial);
}
function assign(node, props, isSVG, skipChildren, prevProps = {}, skipRef = false) {
  props || (props = {});
  for (const prop in prevProps) {
    if (!(prop in props)) {
      if (prop === "children") continue;
      prevProps[prop] = assignProp(node, prop, null, prevProps[prop], isSVG, skipRef);
    }
  }
  for (const prop in props) {
    if (prop === "children") {
      if (!skipChildren) insertExpression(node, props.children);
      continue;
    }
    const value = props[prop];
    prevProps[prop] = assignProp(node, prop, value, prevProps[prop], isSVG, skipRef);
  }
}
function toPropertyName(name) {
  return name.toLowerCase().replace(/-([a-z])/g, (_, w) => w.toUpperCase());
}
function toggleClassKey(node, key, value) {
  const classNames = key.trim().split(/\s+/);
  for (let i = 0, nameLen = classNames.length; i < nameLen; i++) node.classList.toggle(classNames[i], value);
}
function assignProp(node, prop, value, prev, isSVG, skipRef) {
  let isCE, isProp, isChildProp, propAlias, forceProp;
  if (prop === "style") return style(node, value, prev);
  if (prop === "classList") return classList(node, value, prev);
  if (value === prev) return prev;
  if (prop === "ref") {
    if (!skipRef) value(node);
  } else if (prop.slice(0, 3) === "on:") {
    const e = prop.slice(3);
    prev && node.removeEventListener(e, prev);
    value && node.addEventListener(e, value);
  } else if (prop.slice(0, 10) === "oncapture:") {
    const e = prop.slice(10);
    prev && node.removeEventListener(e, prev, true);
    value && node.addEventListener(e, value, true);
  } else if (prop.slice(0, 2) === "on") {
    const name = prop.slice(2).toLowerCase();
    const delegate = DelegatedEvents.has(name);
    if (!delegate && prev) {
      const h = Array.isArray(prev) ? prev[0] : prev;
      node.removeEventListener(name, h);
    }
    if (delegate || value) {
      addEventListener(node, name, value, delegate);
      delegate && delegateEvents([name]);
    }
  } else if (prop.slice(0, 5) === "attr:") {
    setAttribute(node, prop.slice(5), value);
  } else if ((forceProp = prop.slice(0, 5) === "prop:") || (isChildProp = ChildProperties.has(prop)) || !isSVG && ((propAlias = getPropAlias(prop, node.tagName)) || (isProp = Properties.has(prop))) || (isCE = node.nodeName.includes("-"))) {
    if (forceProp) {
      prop = prop.slice(5);
      isProp = true;
    }
    if (prop === "class" || prop === "className") className(node, value);else if (isCE && !isProp && !isChildProp) node[toPropertyName(prop)] = value;else node[propAlias || prop] = value;
  } else {
    const ns = isSVG && prop.indexOf(":") > -1 && SVGNamespace[prop.split(":")[0]];
    if (ns) setAttributeNS(node, ns, prop, value);else setAttribute(node, Aliases[prop] || prop, value);
  }
  return value;
}
function eventHandler(e) {
  const key = `$$${e.type}`;
  let node = e.composedPath && e.composedPath()[0] || e.target;
  if (e.target !== node) {
    Object.defineProperty(e, "target", {
      configurable: true,
      value: node
    });
  }
  Object.defineProperty(e, "currentTarget", {
    configurable: true,
    get() {
      return node || document;
    }
  });
  while (node) {
    const handler = node[key];
    if (handler && !node.disabled) {
      const data = node[`${key}Data`];
      data !== undefined ? handler.call(node, data, e) : handler.call(node, e);
      if (e.cancelBubble) return;
    }
    node = node._$host || node.parentNode || node.host;
  }
}
function insertExpression(parent, value, current, marker, unwrapArray) {
  while (typeof current === "function") current = current();
  if (value === current) return current;
  const t = typeof value,
    multi = marker !== undefined;
  parent = multi && current[0] && current[0].parentNode || parent;
  if (t === "string" || t === "number") {
    if (t === "number") value = value.toString();
    if (multi) {
      let node = current[0];
      if (node && node.nodeType === 3) {
        node.data = value;
      } else node = document.createTextNode(value);
      current = cleanChildren(parent, current, marker, node);
    } else {
      if (current !== "" && typeof current === "string") {
        current = parent.firstChild.data = value;
      } else current = parent.textContent = value;
    }
  } else if (value == null || t === "boolean") {
    current = cleanChildren(parent, current, marker);
  } else if (t === "function") {
    createRenderEffect(() => {
      let v = value();
      while (typeof v === "function") v = v();
      current = insertExpression(parent, v, current, marker);
    });
    return () => current;
  } else if (Array.isArray(value)) {
    const array = [];
    const currentArray = current && Array.isArray(current);
    if (normalizeIncomingArray(array, value, current, unwrapArray)) {
      createRenderEffect(() => current = insertExpression(parent, array, current, marker, true));
      return () => current;
    }
    if (array.length === 0) {
      current = cleanChildren(parent, current, marker);
      if (multi) return current;
    } else if (currentArray) {
      if (current.length === 0) {
        appendNodes(parent, array, marker);
      } else reconcileArrays(parent, current, array);
    } else {
      current && cleanChildren(parent);
      appendNodes(parent, array);
    }
    current = array;
  } else if (value.nodeType) {
    if (Array.isArray(current)) {
      if (multi) return current = cleanChildren(parent, current, marker, value);
      cleanChildren(parent, current, null, value);
    } else if (current == null || current === "" || !parent.firstChild) {
      parent.appendChild(value);
    } else parent.replaceChild(value, parent.firstChild);
    current = value;
  } else console.warn(`Unrecognized value. Skipped inserting`, value);
  return current;
}
function normalizeIncomingArray(normalized, array, current, unwrap) {
  let dynamic = false;
  for (let i = 0, len = array.length; i < len; i++) {
    let item = array[i],
      prev = current && current[i],
      t;
    if (item == null || item === true || item === false) ;else if ((t = typeof item) === "object" && item.nodeType) {
      normalized.push(item);
    } else if (Array.isArray(item)) {
      dynamic = normalizeIncomingArray(normalized, item, prev) || dynamic;
    } else if (t === "function") {
      if (unwrap) {
        while (typeof item === "function") item = item();
        dynamic = normalizeIncomingArray(normalized, Array.isArray(item) ? item : [item], Array.isArray(prev) ? prev : [prev]) || dynamic;
      } else {
        normalized.push(item);
        dynamic = true;
      }
    } else {
      const value = String(item);
      if (prev && prev.nodeType === 3 && prev.data === value) normalized.push(prev);else normalized.push(document.createTextNode(value));
    }
  }
  return dynamic;
}
function appendNodes(parent, array, marker = null) {
  for (let i = 0, len = array.length; i < len; i++) parent.insertBefore(array[i], marker);
}
function cleanChildren(parent, current, marker, replacement) {
  if (marker === undefined) return parent.textContent = "";
  const node = replacement || document.createTextNode("");
  if (current.length) {
    let inserted = false;
    for (let i = current.length - 1; i >= 0; i--) {
      const el = current[i];
      if (node !== el) {
        const isParent = el.parentNode === parent;
        if (!inserted && !i) isParent ? parent.replaceChild(node, el) : parent.insertBefore(node, marker);else isParent && el.remove();
      } else inserted = true;
    }
  } else parent.insertBefore(node, marker);
  return [node];
}

function cloneProps(props) {
  const propKeys = Object.keys(props);
  return propKeys.reduce((memo, k) => {
    const prop = props[k];
    memo[k] = Object.assign({}, prop);
    if (isObject(prop.value) && !isFunction(prop.value) && !Array.isArray(prop.value)) memo[k].value = Object.assign({}, prop.value);
    if (Array.isArray(prop.value)) memo[k].value = prop.value.slice(0);
    return memo;
  }, {});
}
function normalizePropDefs(props) {
  if (!props) return {};
  const propKeys = Object.keys(props);
  return propKeys.reduce((memo, k) => {
    const v = props[k];
    memo[k] = !(isObject(v) && "value" in v) ? {
      value: v
    } : v;
    memo[k].attribute || (memo[k].attribute = toAttribute(k));
    memo[k].parse = "parse" in memo[k] ? memo[k].parse : typeof memo[k].value !== "string";
    return memo;
  }, {});
}
function propValues(props) {
  const propKeys = Object.keys(props);
  return propKeys.reduce((memo, k) => {
    memo[k] = props[k].value;
    return memo;
  }, {});
}
function initializeProps(element, propDefinition) {
  const props = cloneProps(propDefinition),
    propKeys = Object.keys(propDefinition);
  propKeys.forEach(key => {
    const prop = props[key],
      attr = element.getAttribute(prop.attribute),
      value = element[key];
    if (attr) prop.value = prop.parse ? parseAttributeValue(attr) : attr;
    if (value != null) prop.value = Array.isArray(value) ? value.slice(0) : value;
    prop.reflect && reflect(element, prop.attribute, prop.value);
    Object.defineProperty(element, key, {
      get() {
        return prop.value;
      },
      set(val) {
        const oldValue = prop.value;
        prop.value = val;
        prop.reflect && reflect(this, prop.attribute, prop.value);
        for (let i = 0, l = this.__propertyChangedCallbacks.length; i < l; i++) {
          this.__propertyChangedCallbacks[i](key, val, oldValue);
        }
      },
      enumerable: true,
      configurable: true
    });
  });
  return props;
}
function parseAttributeValue(value) {
  if (!value) return;
  try {
    return JSON.parse(value);
  } catch (err) {
    return value;
  }
}
function reflect(node, attribute, value) {
  if (value == null || value === false) return node.removeAttribute(attribute);
  let reflect = JSON.stringify(value);
  node.__updating[attribute] = true;
  if (reflect === "true") reflect = "";
  node.setAttribute(attribute, reflect);
  Promise.resolve().then(() => delete node.__updating[attribute]);
}
function toAttribute(propName) {
  return propName.replace(/\.?([A-Z]+)/g, (x, y) => "-" + y.toLowerCase()).replace("_", "-").replace(/^-/, "");
}
function isObject(obj) {
  return obj != null && (typeof obj === "object" || typeof obj === "function");
}
function isFunction(val) {
  return Object.prototype.toString.call(val) === "[object Function]";
}
function isConstructor(f) {
  return typeof f === "function" && f.toString().indexOf("class") === 0;
}
let currentElement;
function noShadowDOM() {
  Object.defineProperty(currentElement, "renderRoot", {
    value: currentElement
  });
}
function createElementType(BaseElement, propDefinition) {
  const propKeys = Object.keys(propDefinition);
  return class CustomElement extends BaseElement {
    static get observedAttributes() {
      return propKeys.map(k => propDefinition[k].attribute);
    }
    constructor() {
      super();
      this.__initialized = false;
      this.__released = false;
      this.__releaseCallbacks = [];
      this.__propertyChangedCallbacks = [];
      this.__updating = {};
      this.props = {};
    }
    connectedCallback() {
      if (this.__initialized) return;
      this.__releaseCallbacks = [];
      this.__propertyChangedCallbacks = [];
      this.__updating = {};
      this.props = initializeProps(this, propDefinition);
      const props = propValues(this.props),
        ComponentType = this.Component,
        outerElement = currentElement;
      try {
        currentElement = this;
        this.__initialized = true;
        if (isConstructor(ComponentType)) new ComponentType(props, {
          element: this
        });else ComponentType(props, {
          element: this
        });
      } finally {
        currentElement = outerElement;
      }
    }
    async disconnectedCallback() {
      // prevent premature releasing when element is only temporarely removed from DOM
      await Promise.resolve();
      if (this.isConnected) return;
      this.__propertyChangedCallbacks.length = 0;
      let callback = null;
      while (callback = this.__releaseCallbacks.pop()) callback(this);
      delete this.__initialized;
      this.__released = true;
    }
    attributeChangedCallback(name, oldVal, newVal) {
      if (!this.__initialized) return;
      if (this.__updating[name]) return;
      name = this.lookupProp(name);
      if (name in propDefinition) {
        if (newVal == null && !this[name]) return;
        this[name] = propDefinition[name].parse ? parseAttributeValue(newVal) : newVal;
      }
    }
    lookupProp(attrName) {
      if (!propDefinition) return;
      return propKeys.find(k => attrName === k || attrName === propDefinition[k].attribute);
    }
    get renderRoot() {
      return this.shadowRoot || this.attachShadow({
        mode: "open"
      });
    }
    addReleaseCallback(fn) {
      this.__releaseCallbacks.push(fn);
    }
    addPropertyChangedCallback(fn) {
      this.__propertyChangedCallbacks.push(fn);
    }
  };
}
function register(tag, props = {}, options = {}) {
  const {
    BaseElement = HTMLElement,
    extension
  } = options;
  return ComponentType => {
    if (!tag) throw new Error("tag is required to register a Component");
    let ElementType = customElements.get(tag);
    if (ElementType) {
      // Consider disabling this in a production mode
      ElementType.prototype.Component = ComponentType;
      return ElementType;
    }
    ElementType = createElementType(BaseElement, normalizePropDefs(props));
    ElementType.prototype.Component = ComponentType;
    ElementType.prototype.registeredTag = tag;
    customElements.define(tag, ElementType, extension);
    return ElementType;
  };
}

function createProps(raw) {
  const keys = Object.keys(raw);
  const props = {};
  for (let i = 0; i < keys.length; i++) {
    const [get, set] = createSignal(raw[keys[i]]);
    Object.defineProperty(props, keys[i], {
      get,
      set(v) {
        set(() => v);
      }
    });
  }
  return props;
}
function lookupContext(el) {
  if (el.assignedSlot && el.assignedSlot._$owner) return el.assignedSlot._$owner;
  let next = el.parentNode;
  while (next && !next._$owner && !(next.assignedSlot && next.assignedSlot._$owner)) next = next.parentNode;
  return next && next.assignedSlot ? next.assignedSlot._$owner : el._$owner;
}
function withSolid(ComponentType) {
  return (rawProps, options) => {
    const {
      element
    } = options;
    return createRoot(dispose => {
      const props = createProps(rawProps);
      element.addPropertyChangedCallback((key, val) => props[key] = val);
      element.addReleaseCallback(() => {
        element.renderRoot.textContent = "";
        dispose();
      });
      const comp = ComponentType(props, options);
      return insert(element.renderRoot, comp);
    }, lookupContext(element));
  };
}
function customElement(tag, props, ComponentType) {
  if (arguments.length === 2) {
    ComponentType = props;
    props = {};
  }
  return register(tag, props)(withSolid(ComponentType));
}

function r(e){var t,f,n="";if("string"==typeof e||"number"==typeof e)n+=e;else if("object"==typeof e)if(Array.isArray(e))for(t=0;t<e.length;t++)e[t]&&(f=r(e[t]))&&(n&&(n+=" "),n+=f);else for(t in e)e[t]&&(n&&(n+=" "),n+=t);return n}function clsx(){for(var e,t,f=0,n="";f<arguments.length;)(e=arguments[f++])&&(t=r(e))&&(n&&(n+=" "),n+=t);return n}

var styles$d = {"tabs":"TabControl-module_tabs__zTPtt","tab":"TabControl-module_tab__Kv864","activeTab":"TabControl-module_activeTab__U3seR"};

const _tmpl$$n = /*#__PURE__*/template(`<nav>`),
  _tmpl$2$9 = /*#__PURE__*/template(`<button>`);
function TabControl(props) {
  return (() => {
    const _el$ = _tmpl$$n();
    insert(_el$, createComponent(For, {
      get each() {
        return props.tabs;
      },
      children: tab => (() => {
        const _el$2 = _tmpl$2$9();
        _el$2.$$click = () => props.onTabSelect(tab.id);
        insert(_el$2, () => tab.displayName);
        createRenderEffect(_p$ => {
          const _v$3 = styles$d.tab,
            _v$4 = {
              [styles$d.activeTab]: props.activeTabId === tab.id
            };
          _v$3 !== _p$._v$3 && className(_el$2, _p$._v$3 = _v$3);
          _p$._v$4 = classList(_el$2, _v$4, _p$._v$4);
          return _p$;
        }, {
          _v$3: undefined,
          _v$4: undefined
        });
        return _el$2;
      })()
    }));
    createRenderEffect(_p$ => {
      const _v$ = clsx(styles$d.tabs, props.class),
        _v$2 = props.style;
      _v$ !== _p$._v$ && className(_el$, _p$._v$ = _v$);
      _p$._v$2 = style(_el$, _v$2, _p$._v$2);
      return _p$;
    }, {
      _v$: undefined,
      _v$2: undefined
    });
    return _el$;
  })();
}
delegateEvents(["click"]);

const translationTables = {
  "en-US": {
    form: {
      actions: {
        logIn: {
          tabName: "Log in",
          usernameless: {
            title: "Sign in with app.",
            description: {
              appLink: "Click the button to open the Auth Armor app.",
              qrCode: "Scan the code below with the Auth Armor app."
            },
            verificationCode: verificationCode => ["Your verification code is ", verificationCode, "."],
            retry: "Try again"
          },
          username: {
            title: "Sign in with username or email.",
            fields: {
              username: {
                label: "Username or email address"
              }
            },
            submit: "Continue"
          },
          methodButtons: {
            username: "Sign in with username or email instead",
            usernameless: "Sign in with app instead"
          },
          prompt: username => ["Authenticating as ", username, "."]
        },
        register: {
          tabName: "Register",
          username: {
            title: "Sign up.",
            fields: {
              username: {
                label: "Username or email address"
              }
            },
            submit: "Continue"
          },
          prompt: username => ["Registering as ", username, "."]
        }
      },
      back: "Back",
      methods: {
        prompt: "Select authentication method.",
        authenticator: {
          label: "Authenticator app",
          description: "Receive a push notification or scan a QR code using your Auth Armor app."
        },
        magicLinkEmail: {
          label: "Magic link email",
          description: "Receive an email with a link to authenticate yourself."
        },
        passkey: {
          label: "Passkey",
          description: "Sign in using your browser’s built-in secure authentication mechanism."
        }
      },
      prompts: {
        authenticator: {
          logIn: {
            title: "Authenticate with authenticator app.",
            description: {
              qrCode: "We’ve sent a push notification to your authenticator app. If that’s not working, you can scan the code below instead.",
              appLink: "We’ve sent a push notification to your authenticator app. If that’s not working, you can click the button below instead."
            }
          },
          register: {
            title: "Register with authenticator app.",
            description: {
              qrCode: "Scan the code below with your phone. We’ll prompt you to download the Auth Armor app if it isn’t already installed.",
              appLink: "Click the button below. We’ll prompt you to download the Auth Armor app if it isn’t already installed."
            }
          }
        },
        magicLinkEmail: {
          logIn: {
            title: "Authenticate with magic link email.",
            description: "We’ve sent you an email with a link to log you in.",
            outOfBandCompletionInfo: "You’ve logged in on a different tab or window."
          },
          register: {
            title: "Register with magic link email.",
            description: "We’ve sent you an email with a link to register you.",
            outOfBandCompletionInfo: "You’ve registered on a different tab or window."
          }
        },
        passkey: {
          logIn: {
            title: "Authenticate with passkey.",
            description: "Please check your browser or security device for instructions."
          },
          register: {
            title: "Register with passkey.",
            description: "Please check your browser or security device for instructions."
          }
        },
        wait: {
          title: "Please wait.",
          description: "We’re processing your request."
        },
        captcha: {
          title: "Verification needed.",
          description: "We need to make sure you’re not a robot."
        }
      },
      errors: {
        usernamelessLogIn: {
          declined: "You declined the request.",
          network: "A network error occurred. Please check the console.",
          unknown: "An unknown error occurred. The console may have further details."
        },
        usernameLogIn: {
          userNotFound: "The requested user does not exist.",
          noAvailableMethods: "There are no available methods for the user.",
          network: "A network error occurred. Please check the console.",
          unknown: "An unknown error occurred. The console may have further details."
        },
        usernameRegister: {
          userAlreadyExists: "This username is already taken.",
          noAvailableMethods: "There are no available methods to use.",
          network: "A network error occurred. Please check the console.",
          unknown: "An unknown error occurred. The console may have further details."
        },
        authenticator: {
          timedOut: "The request timed out.",
          declined: "You declined the request.",
          network: "A network error occurred. Please check the console.",
          unknown: "An unknown error occurred. The console may have further details."
        },
        magicLinkEmail: {
          network: "A network error occurred. Please check the console.",
          unknown: "An unknown error occurred. The console may have further details."
        },
        passkey: {
          declined: "We were unable to process the request. This may have occurred because you declined the prompt.",
          network: "A network error occurred. Please check the console.",
          unknown: "An unknown error occurred. The console may have further details."
        },
        recover: "Go back and try again."
      }
    }
  }
};
const defaultTranslationTable = translationTables["en-US"];

const TranslationTableContext = createContext();

function useTranslationTable() {
  return useContext(TranslationTableContext);
}

var styles$c = {"appLinkLoader":"AppLinkLoader-module_appLinkLoader__m7C-d","activeAppLinkLoader":"AppLinkLoader-module_activeAppLinkLoader__ndgsL","lds-grid":"AppLinkLoader-module_lds-grid__Il7QH"};

const _tmpl$$m = /*#__PURE__*/template(`<div>`);
function AppLinkLoader(props) {
  return (() => {
    const _el$ = _tmpl$$m();
    insert(_el$, () => [...new Array(3)].map(() => _tmpl$$m()));
    createRenderEffect(_p$ => {
      const _v$ = clsx(styles$c.appLinkLoader, {
          [styles$c.activeAppLinkLoader]: props.isActive
        }, props.class),
        _v$2 = props.style;
      _v$ !== _p$._v$ && className(_el$, _p$._v$ = _v$);
      _p$._v$2 = style(_el$, _v$2, _p$._v$2);
      return _p$;
    }, {
      _v$: undefined,
      _v$2: undefined
    });
    return _el$;
  })();
}

var styles$b = {"link":"AppLink-module_link__cHJ35","icon":"AppLink-module_icon__2DYVJ","label":"AppLink-module_label__NR-l2"};

var img$3 = "data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' class='feather feather-shield'%3e%3cpath d='M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z'%3e%3c/path%3e%3c/svg%3e";

const _tmpl$$l = /*#__PURE__*/template(`<a><img alt=""><p>Open Auth Armor App`);
function AppLink(props) {
  let existingWindow = null;
  onCleanup(() => {
    existingWindow?.close();
  });
  const handleClick = event => {
    existingWindow?.close();
    existingWindow = window.open(props.data, "_blank");
    if (existingWindow !== null) {
      event.preventDefault();
    }
  };
  return (() => {
    const _el$ = _tmpl$$l(),
      _el$2 = _el$.firstChild,
      _el$3 = _el$2.nextSibling;
    _el$.$$click = handleClick;
    setAttribute(_el$2, "src", img$3);
    createRenderEffect(_p$ => {
      const _v$ = props.data,
        _v$2 = clsx(styles$b.link, props.class),
        _v$3 = props.style,
        _v$4 = styles$b.icon,
        _v$5 = styles$b.label;
      _v$ !== _p$._v$ && setAttribute(_el$, "href", _p$._v$ = _v$);
      _v$2 !== _p$._v$2 && className(_el$, _p$._v$2 = _v$2);
      _p$._v$3 = style(_el$, _v$3, _p$._v$3);
      _v$4 !== _p$._v$4 && className(_el$2, _p$._v$4 = _v$4);
      _v$5 !== _p$._v$5 && className(_el$3, _p$._v$5 = _v$5);
      return _p$;
    }, {
      _v$: undefined,
      _v$2: undefined,
      _v$3: undefined,
      _v$4: undefined,
      _v$5: undefined
    });
    return _el$;
  })();
}
delegateEvents(["click"]);

var styles$a = {"qrCodeLoader":"QrCodeLoader-module_qrCodeLoader__zTqFs","activeQrCodeLoader":"QrCodeLoader-module_activeQrCodeLoader__uXGzM","lds-grid":"QrCodeLoader-module_lds-grid__O2668"};

const _tmpl$$k = /*#__PURE__*/template(`<div>`);
function QrCodeLoader(props) {
  return (() => {
    const _el$ = _tmpl$$k();
    insert(_el$, () => [...new Array(9)].map(() => _tmpl$$k()));
    createRenderEffect(_p$ => {
      const _v$ = clsx(styles$a.qrCodeLoader, {
          [styles$a.activeQrCodeLoader]: props.isActive
        }, props.class),
        _v$2 = props.style;
      _v$ !== _p$._v$ && className(_el$, _p$._v$ = _v$);
      _p$._v$2 = style(_el$, _v$2, _p$._v$2);
      return _p$;
    }, {
      _v$: undefined,
      _v$2: undefined
    });
    return _el$;
  })();
}

function getDefaultExportFromCjs (x) {
	return x && x.__esModule && Object.prototype.hasOwnProperty.call(x, 'default') ? x['default'] : x;
}

var qrcode$1 = {exports: {}};

(function (module, exports) {
	//---------------------------------------------------------------------
	//
	// QR Code Generator for JavaScript
	//
	// Copyright (c) 2009 Kazuhiko Arase
	//
	// URL: http://www.d-project.com/
	//
	// Licensed under the MIT license:
	//  http://www.opensource.org/licenses/mit-license.php
	//
	// The word 'QR Code' is registered trademark of
	// DENSO WAVE INCORPORATED
	//  http://www.denso-wave.com/qrcode/faqpatent-e.html
	//
	//---------------------------------------------------------------------

	var qrcode = function () {
	  //---------------------------------------------------------------------
	  // qrcode
	  //---------------------------------------------------------------------

	  /**
	   * qrcode
	   * @param typeNumber 1 to 40
	   * @param errorCorrectionLevel 'L','M','Q','H'
	   */
	  var qrcode = function (typeNumber, errorCorrectionLevel) {
	    var PAD0 = 0xEC;
	    var PAD1 = 0x11;
	    var _typeNumber = typeNumber;
	    var _errorCorrectionLevel = QRErrorCorrectionLevel[errorCorrectionLevel];
	    var _modules = null;
	    var _moduleCount = 0;
	    var _dataCache = null;
	    var _dataList = [];
	    var _this = {};
	    var makeImpl = function (test, maskPattern) {
	      _moduleCount = _typeNumber * 4 + 17;
	      _modules = function (moduleCount) {
	        var modules = new Array(moduleCount);
	        for (var row = 0; row < moduleCount; row += 1) {
	          modules[row] = new Array(moduleCount);
	          for (var col = 0; col < moduleCount; col += 1) {
	            modules[row][col] = null;
	          }
	        }
	        return modules;
	      }(_moduleCount);
	      setupPositionProbePattern(0, 0);
	      setupPositionProbePattern(_moduleCount - 7, 0);
	      setupPositionProbePattern(0, _moduleCount - 7);
	      setupPositionAdjustPattern();
	      setupTimingPattern();
	      setupTypeInfo(test, maskPattern);
	      if (_typeNumber >= 7) {
	        setupTypeNumber(test);
	      }
	      if (_dataCache == null) {
	        _dataCache = createData(_typeNumber, _errorCorrectionLevel, _dataList);
	      }
	      mapData(_dataCache, maskPattern);
	    };
	    var setupPositionProbePattern = function (row, col) {
	      for (var r = -1; r <= 7; r += 1) {
	        if (row + r <= -1 || _moduleCount <= row + r) continue;
	        for (var c = -1; c <= 7; c += 1) {
	          if (col + c <= -1 || _moduleCount <= col + c) continue;
	          if (0 <= r && r <= 6 && (c == 0 || c == 6) || 0 <= c && c <= 6 && (r == 0 || r == 6) || 2 <= r && r <= 4 && 2 <= c && c <= 4) {
	            _modules[row + r][col + c] = true;
	          } else {
	            _modules[row + r][col + c] = false;
	          }
	        }
	      }
	    };
	    var getBestMaskPattern = function () {
	      var minLostPoint = 0;
	      var pattern = 0;
	      for (var i = 0; i < 8; i += 1) {
	        makeImpl(true, i);
	        var lostPoint = QRUtil.getLostPoint(_this);
	        if (i == 0 || minLostPoint > lostPoint) {
	          minLostPoint = lostPoint;
	          pattern = i;
	        }
	      }
	      return pattern;
	    };
	    var setupTimingPattern = function () {
	      for (var r = 8; r < _moduleCount - 8; r += 1) {
	        if (_modules[r][6] != null) {
	          continue;
	        }
	        _modules[r][6] = r % 2 == 0;
	      }
	      for (var c = 8; c < _moduleCount - 8; c += 1) {
	        if (_modules[6][c] != null) {
	          continue;
	        }
	        _modules[6][c] = c % 2 == 0;
	      }
	    };
	    var setupPositionAdjustPattern = function () {
	      var pos = QRUtil.getPatternPosition(_typeNumber);
	      for (var i = 0; i < pos.length; i += 1) {
	        for (var j = 0; j < pos.length; j += 1) {
	          var row = pos[i];
	          var col = pos[j];
	          if (_modules[row][col] != null) {
	            continue;
	          }
	          for (var r = -2; r <= 2; r += 1) {
	            for (var c = -2; c <= 2; c += 1) {
	              if (r == -2 || r == 2 || c == -2 || c == 2 || r == 0 && c == 0) {
	                _modules[row + r][col + c] = true;
	              } else {
	                _modules[row + r][col + c] = false;
	              }
	            }
	          }
	        }
	      }
	    };
	    var setupTypeNumber = function (test) {
	      var bits = QRUtil.getBCHTypeNumber(_typeNumber);
	      for (var i = 0; i < 18; i += 1) {
	        var mod = !test && (bits >> i & 1) == 1;
	        _modules[Math.floor(i / 3)][i % 3 + _moduleCount - 8 - 3] = mod;
	      }
	      for (var i = 0; i < 18; i += 1) {
	        var mod = !test && (bits >> i & 1) == 1;
	        _modules[i % 3 + _moduleCount - 8 - 3][Math.floor(i / 3)] = mod;
	      }
	    };
	    var setupTypeInfo = function (test, maskPattern) {
	      var data = _errorCorrectionLevel << 3 | maskPattern;
	      var bits = QRUtil.getBCHTypeInfo(data);

	      // vertical
	      for (var i = 0; i < 15; i += 1) {
	        var mod = !test && (bits >> i & 1) == 1;
	        if (i < 6) {
	          _modules[i][8] = mod;
	        } else if (i < 8) {
	          _modules[i + 1][8] = mod;
	        } else {
	          _modules[_moduleCount - 15 + i][8] = mod;
	        }
	      }

	      // horizontal
	      for (var i = 0; i < 15; i += 1) {
	        var mod = !test && (bits >> i & 1) == 1;
	        if (i < 8) {
	          _modules[8][_moduleCount - i - 1] = mod;
	        } else if (i < 9) {
	          _modules[8][15 - i - 1 + 1] = mod;
	        } else {
	          _modules[8][15 - i - 1] = mod;
	        }
	      }

	      // fixed module
	      _modules[_moduleCount - 8][8] = !test;
	    };
	    var mapData = function (data, maskPattern) {
	      var inc = -1;
	      var row = _moduleCount - 1;
	      var bitIndex = 7;
	      var byteIndex = 0;
	      var maskFunc = QRUtil.getMaskFunction(maskPattern);
	      for (var col = _moduleCount - 1; col > 0; col -= 2) {
	        if (col == 6) col -= 1;
	        while (true) {
	          for (var c = 0; c < 2; c += 1) {
	            if (_modules[row][col - c] == null) {
	              var dark = false;
	              if (byteIndex < data.length) {
	                dark = (data[byteIndex] >>> bitIndex & 1) == 1;
	              }
	              var mask = maskFunc(row, col - c);
	              if (mask) {
	                dark = !dark;
	              }
	              _modules[row][col - c] = dark;
	              bitIndex -= 1;
	              if (bitIndex == -1) {
	                byteIndex += 1;
	                bitIndex = 7;
	              }
	            }
	          }
	          row += inc;
	          if (row < 0 || _moduleCount <= row) {
	            row -= inc;
	            inc = -inc;
	            break;
	          }
	        }
	      }
	    };
	    var createBytes = function (buffer, rsBlocks) {
	      var offset = 0;
	      var maxDcCount = 0;
	      var maxEcCount = 0;
	      var dcdata = new Array(rsBlocks.length);
	      var ecdata = new Array(rsBlocks.length);
	      for (var r = 0; r < rsBlocks.length; r += 1) {
	        var dcCount = rsBlocks[r].dataCount;
	        var ecCount = rsBlocks[r].totalCount - dcCount;
	        maxDcCount = Math.max(maxDcCount, dcCount);
	        maxEcCount = Math.max(maxEcCount, ecCount);
	        dcdata[r] = new Array(dcCount);
	        for (var i = 0; i < dcdata[r].length; i += 1) {
	          dcdata[r][i] = 0xff & buffer.getBuffer()[i + offset];
	        }
	        offset += dcCount;
	        var rsPoly = QRUtil.getErrorCorrectPolynomial(ecCount);
	        var rawPoly = qrPolynomial(dcdata[r], rsPoly.getLength() - 1);
	        var modPoly = rawPoly.mod(rsPoly);
	        ecdata[r] = new Array(rsPoly.getLength() - 1);
	        for (var i = 0; i < ecdata[r].length; i += 1) {
	          var modIndex = i + modPoly.getLength() - ecdata[r].length;
	          ecdata[r][i] = modIndex >= 0 ? modPoly.getAt(modIndex) : 0;
	        }
	      }
	      var totalCodeCount = 0;
	      for (var i = 0; i < rsBlocks.length; i += 1) {
	        totalCodeCount += rsBlocks[i].totalCount;
	      }
	      var data = new Array(totalCodeCount);
	      var index = 0;
	      for (var i = 0; i < maxDcCount; i += 1) {
	        for (var r = 0; r < rsBlocks.length; r += 1) {
	          if (i < dcdata[r].length) {
	            data[index] = dcdata[r][i];
	            index += 1;
	          }
	        }
	      }
	      for (var i = 0; i < maxEcCount; i += 1) {
	        for (var r = 0; r < rsBlocks.length; r += 1) {
	          if (i < ecdata[r].length) {
	            data[index] = ecdata[r][i];
	            index += 1;
	          }
	        }
	      }
	      return data;
	    };
	    var createData = function (typeNumber, errorCorrectionLevel, dataList) {
	      var rsBlocks = QRRSBlock.getRSBlocks(typeNumber, errorCorrectionLevel);
	      var buffer = qrBitBuffer();
	      for (var i = 0; i < dataList.length; i += 1) {
	        var data = dataList[i];
	        buffer.put(data.getMode(), 4);
	        buffer.put(data.getLength(), QRUtil.getLengthInBits(data.getMode(), typeNumber));
	        data.write(buffer);
	      }

	      // calc num max data.
	      var totalDataCount = 0;
	      for (var i = 0; i < rsBlocks.length; i += 1) {
	        totalDataCount += rsBlocks[i].dataCount;
	      }
	      if (buffer.getLengthInBits() > totalDataCount * 8) {
	        throw 'code length overflow. (' + buffer.getLengthInBits() + '>' + totalDataCount * 8 + ')';
	      }

	      // end code
	      if (buffer.getLengthInBits() + 4 <= totalDataCount * 8) {
	        buffer.put(0, 4);
	      }

	      // padding
	      while (buffer.getLengthInBits() % 8 != 0) {
	        buffer.putBit(false);
	      }

	      // padding
	      while (true) {
	        if (buffer.getLengthInBits() >= totalDataCount * 8) {
	          break;
	        }
	        buffer.put(PAD0, 8);
	        if (buffer.getLengthInBits() >= totalDataCount * 8) {
	          break;
	        }
	        buffer.put(PAD1, 8);
	      }
	      return createBytes(buffer, rsBlocks);
	    };
	    _this.addData = function (data, mode) {
	      mode = mode || 'Byte';
	      var newData = null;
	      switch (mode) {
	        case 'Numeric':
	          newData = qrNumber(data);
	          break;
	        case 'Alphanumeric':
	          newData = qrAlphaNum(data);
	          break;
	        case 'Byte':
	          newData = qr8BitByte(data);
	          break;
	        case 'Kanji':
	          newData = qrKanji(data);
	          break;
	        default:
	          throw 'mode:' + mode;
	      }
	      _dataList.push(newData);
	      _dataCache = null;
	    };
	    _this.isDark = function (row, col) {
	      if (row < 0 || _moduleCount <= row || col < 0 || _moduleCount <= col) {
	        throw row + ',' + col;
	      }
	      return _modules[row][col];
	    };
	    _this.getModuleCount = function () {
	      return _moduleCount;
	    };
	    _this.make = function () {
	      if (_typeNumber < 1) {
	        var typeNumber = 1;
	        for (; typeNumber < 40; typeNumber++) {
	          var rsBlocks = QRRSBlock.getRSBlocks(typeNumber, _errorCorrectionLevel);
	          var buffer = qrBitBuffer();
	          for (var i = 0; i < _dataList.length; i++) {
	            var data = _dataList[i];
	            buffer.put(data.getMode(), 4);
	            buffer.put(data.getLength(), QRUtil.getLengthInBits(data.getMode(), typeNumber));
	            data.write(buffer);
	          }
	          var totalDataCount = 0;
	          for (var i = 0; i < rsBlocks.length; i++) {
	            totalDataCount += rsBlocks[i].dataCount;
	          }
	          if (buffer.getLengthInBits() <= totalDataCount * 8) {
	            break;
	          }
	        }
	        _typeNumber = typeNumber;
	      }
	      makeImpl(false, getBestMaskPattern());
	    };
	    _this.createTableTag = function (cellSize, margin) {
	      cellSize = cellSize || 2;
	      margin = typeof margin == 'undefined' ? cellSize * 4 : margin;
	      var qrHtml = '';
	      qrHtml += '<table style="';
	      qrHtml += ' border-width: 0px; border-style: none;';
	      qrHtml += ' border-collapse: collapse;';
	      qrHtml += ' padding: 0px; margin: ' + margin + 'px;';
	      qrHtml += '">';
	      qrHtml += '<tbody>';
	      for (var r = 0; r < _this.getModuleCount(); r += 1) {
	        qrHtml += '<tr>';
	        for (var c = 0; c < _this.getModuleCount(); c += 1) {
	          qrHtml += '<td style="';
	          qrHtml += ' border-width: 0px; border-style: none;';
	          qrHtml += ' border-collapse: collapse;';
	          qrHtml += ' padding: 0px; margin: 0px;';
	          qrHtml += ' width: ' + cellSize + 'px;';
	          qrHtml += ' height: ' + cellSize + 'px;';
	          qrHtml += ' background-color: ';
	          qrHtml += _this.isDark(r, c) ? '#000000' : '#ffffff';
	          qrHtml += ';';
	          qrHtml += '"/>';
	        }
	        qrHtml += '</tr>';
	      }
	      qrHtml += '</tbody>';
	      qrHtml += '</table>';
	      return qrHtml;
	    };
	    _this.createSvgTag = function (cellSize, margin, alt, title) {
	      var opts = {};
	      if (typeof arguments[0] == 'object') {
	        // Called by options.
	        opts = arguments[0];
	        // overwrite cellSize and margin.
	        cellSize = opts.cellSize;
	        margin = opts.margin;
	        alt = opts.alt;
	        title = opts.title;
	      }
	      cellSize = cellSize || 2;
	      margin = typeof margin == 'undefined' ? cellSize * 4 : margin;

	      // Compose alt property surrogate
	      alt = typeof alt === 'string' ? {
	        text: alt
	      } : alt || {};
	      alt.text = alt.text || null;
	      alt.id = alt.text ? alt.id || 'qrcode-description' : null;

	      // Compose title property surrogate
	      title = typeof title === 'string' ? {
	        text: title
	      } : title || {};
	      title.text = title.text || null;
	      title.id = title.text ? title.id || 'qrcode-title' : null;
	      var size = _this.getModuleCount() * cellSize + margin * 2;
	      var c,
	        mc,
	        r,
	        mr,
	        qrSvg = '',
	        rect;
	      rect = 'l' + cellSize + ',0 0,' + cellSize + ' -' + cellSize + ',0 0,-' + cellSize + 'z ';
	      qrSvg += '<svg version="1.1" xmlns="http://www.w3.org/2000/svg"';
	      qrSvg += !opts.scalable ? ' width="' + size + 'px" height="' + size + 'px"' : '';
	      qrSvg += ' viewBox="0 0 ' + size + ' ' + size + '" ';
	      qrSvg += ' preserveAspectRatio="xMinYMin meet"';
	      qrSvg += title.text || alt.text ? ' role="img" aria-labelledby="' + escapeXml([title.id, alt.id].join(' ').trim()) + '"' : '';
	      qrSvg += '>';
	      qrSvg += title.text ? '<title id="' + escapeXml(title.id) + '">' + escapeXml(title.text) + '</title>' : '';
	      qrSvg += alt.text ? '<description id="' + escapeXml(alt.id) + '">' + escapeXml(alt.text) + '</description>' : '';
	      qrSvg += '<rect width="100%" height="100%" fill="white" cx="0" cy="0"/>';
	      qrSvg += '<path d="';
	      for (r = 0; r < _this.getModuleCount(); r += 1) {
	        mr = r * cellSize + margin;
	        for (c = 0; c < _this.getModuleCount(); c += 1) {
	          if (_this.isDark(r, c)) {
	            mc = c * cellSize + margin;
	            qrSvg += 'M' + mc + ',' + mr + rect;
	          }
	        }
	      }
	      qrSvg += '" stroke="transparent" fill="black"/>';
	      qrSvg += '</svg>';
	      return qrSvg;
	    };
	    _this.createDataURL = function (cellSize, margin) {
	      cellSize = cellSize || 2;
	      margin = typeof margin == 'undefined' ? cellSize * 4 : margin;
	      var size = _this.getModuleCount() * cellSize + margin * 2;
	      var min = margin;
	      var max = size - margin;
	      return createDataURL(size, size, function (x, y) {
	        if (min <= x && x < max && min <= y && y < max) {
	          var c = Math.floor((x - min) / cellSize);
	          var r = Math.floor((y - min) / cellSize);
	          return _this.isDark(r, c) ? 0 : 1;
	        } else {
	          return 1;
	        }
	      });
	    };
	    _this.createImgTag = function (cellSize, margin, alt) {
	      cellSize = cellSize || 2;
	      margin = typeof margin == 'undefined' ? cellSize * 4 : margin;
	      var size = _this.getModuleCount() * cellSize + margin * 2;
	      var img = '';
	      img += '<img';
	      img += '\u0020src="';
	      img += _this.createDataURL(cellSize, margin);
	      img += '"';
	      img += '\u0020width="';
	      img += size;
	      img += '"';
	      img += '\u0020height="';
	      img += size;
	      img += '"';
	      if (alt) {
	        img += '\u0020alt="';
	        img += escapeXml(alt);
	        img += '"';
	      }
	      img += '/>';
	      return img;
	    };
	    var escapeXml = function (s) {
	      var escaped = '';
	      for (var i = 0; i < s.length; i += 1) {
	        var c = s.charAt(i);
	        switch (c) {
	          case '<':
	            escaped += '&lt;';
	            break;
	          case '>':
	            escaped += '&gt;';
	            break;
	          case '&':
	            escaped += '&amp;';
	            break;
	          case '"':
	            escaped += '&quot;';
	            break;
	          default:
	            escaped += c;
	            break;
	        }
	      }
	      return escaped;
	    };
	    var _createHalfASCII = function (margin) {
	      var cellSize = 1;
	      margin = typeof margin == 'undefined' ? cellSize * 2 : margin;
	      var size = _this.getModuleCount() * cellSize + margin * 2;
	      var min = margin;
	      var max = size - margin;
	      var y, x, r1, r2, p;
	      var blocks = {
	        '██': '█',
	        '█ ': '▀',
	        ' █': '▄',
	        '  ': ' '
	      };
	      var blocksLastLineNoMargin = {
	        '██': '▀',
	        '█ ': '▀',
	        ' █': ' ',
	        '  ': ' '
	      };
	      var ascii = '';
	      for (y = 0; y < size; y += 2) {
	        r1 = Math.floor((y - min) / cellSize);
	        r2 = Math.floor((y + 1 - min) / cellSize);
	        for (x = 0; x < size; x += 1) {
	          p = '█';
	          if (min <= x && x < max && min <= y && y < max && _this.isDark(r1, Math.floor((x - min) / cellSize))) {
	            p = ' ';
	          }
	          if (min <= x && x < max && min <= y + 1 && y + 1 < max && _this.isDark(r2, Math.floor((x - min) / cellSize))) {
	            p += ' ';
	          } else {
	            p += '█';
	          }

	          // Output 2 characters per pixel, to create full square. 1 character per pixels gives only half width of square.
	          ascii += margin < 1 && y + 1 >= max ? blocksLastLineNoMargin[p] : blocks[p];
	        }
	        ascii += '\n';
	      }
	      if (size % 2 && margin > 0) {
	        return ascii.substring(0, ascii.length - size - 1) + Array(size + 1).join('▀');
	      }
	      return ascii.substring(0, ascii.length - 1);
	    };
	    _this.createASCII = function (cellSize, margin) {
	      cellSize = cellSize || 1;
	      if (cellSize < 2) {
	        return _createHalfASCII(margin);
	      }
	      cellSize -= 1;
	      margin = typeof margin == 'undefined' ? cellSize * 2 : margin;
	      var size = _this.getModuleCount() * cellSize + margin * 2;
	      var min = margin;
	      var max = size - margin;
	      var y, x, r, p;
	      var white = Array(cellSize + 1).join('██');
	      var black = Array(cellSize + 1).join('  ');
	      var ascii = '';
	      var line = '';
	      for (y = 0; y < size; y += 1) {
	        r = Math.floor((y - min) / cellSize);
	        line = '';
	        for (x = 0; x < size; x += 1) {
	          p = 1;
	          if (min <= x && x < max && min <= y && y < max && _this.isDark(r, Math.floor((x - min) / cellSize))) {
	            p = 0;
	          }

	          // Output 2 characters per pixel, to create full square. 1 character per pixels gives only half width of square.
	          line += p ? white : black;
	        }
	        for (r = 0; r < cellSize; r += 1) {
	          ascii += line + '\n';
	        }
	      }
	      return ascii.substring(0, ascii.length - 1);
	    };
	    _this.renderTo2dContext = function (context, cellSize) {
	      cellSize = cellSize || 2;
	      var length = _this.getModuleCount();
	      for (var row = 0; row < length; row++) {
	        for (var col = 0; col < length; col++) {
	          context.fillStyle = _this.isDark(row, col) ? 'black' : 'white';
	          context.fillRect(row * cellSize, col * cellSize, cellSize, cellSize);
	        }
	      }
	    };
	    return _this;
	  };

	  //---------------------------------------------------------------------
	  // qrcode.stringToBytes
	  //---------------------------------------------------------------------

	  qrcode.stringToBytesFuncs = {
	    'default': function (s) {
	      var bytes = [];
	      for (var i = 0; i < s.length; i += 1) {
	        var c = s.charCodeAt(i);
	        bytes.push(c & 0xff);
	      }
	      return bytes;
	    }
	  };
	  qrcode.stringToBytes = qrcode.stringToBytesFuncs['default'];

	  //---------------------------------------------------------------------
	  // qrcode.createStringToBytes
	  //---------------------------------------------------------------------

	  /**
	   * @param unicodeData base64 string of byte array.
	   * [16bit Unicode],[16bit Bytes], ...
	   * @param numChars
	   */
	  qrcode.createStringToBytes = function (unicodeData, numChars) {
	    // create conversion map.

	    var unicodeMap = function () {
	      var bin = base64DecodeInputStream(unicodeData);
	      var read = function () {
	        var b = bin.read();
	        if (b == -1) throw 'eof';
	        return b;
	      };
	      var count = 0;
	      var unicodeMap = {};
	      while (true) {
	        var b0 = bin.read();
	        if (b0 == -1) break;
	        var b1 = read();
	        var b2 = read();
	        var b3 = read();
	        var k = String.fromCharCode(b0 << 8 | b1);
	        var v = b2 << 8 | b3;
	        unicodeMap[k] = v;
	        count += 1;
	      }
	      if (count != numChars) {
	        throw count + ' != ' + numChars;
	      }
	      return unicodeMap;
	    }();
	    var unknownChar = '?'.charCodeAt(0);
	    return function (s) {
	      var bytes = [];
	      for (var i = 0; i < s.length; i += 1) {
	        var c = s.charCodeAt(i);
	        if (c < 128) {
	          bytes.push(c);
	        } else {
	          var b = unicodeMap[s.charAt(i)];
	          if (typeof b == 'number') {
	            if ((b & 0xff) == b) {
	              // 1byte
	              bytes.push(b);
	            } else {
	              // 2bytes
	              bytes.push(b >>> 8);
	              bytes.push(b & 0xff);
	            }
	          } else {
	            bytes.push(unknownChar);
	          }
	        }
	      }
	      return bytes;
	    };
	  };

	  //---------------------------------------------------------------------
	  // QRMode
	  //---------------------------------------------------------------------

	  var QRMode = {
	    MODE_NUMBER: 1 << 0,
	    MODE_ALPHA_NUM: 1 << 1,
	    MODE_8BIT_BYTE: 1 << 2,
	    MODE_KANJI: 1 << 3
	  };

	  //---------------------------------------------------------------------
	  // QRErrorCorrectionLevel
	  //---------------------------------------------------------------------

	  var QRErrorCorrectionLevel = {
	    L: 1,
	    M: 0,
	    Q: 3,
	    H: 2
	  };

	  //---------------------------------------------------------------------
	  // QRMaskPattern
	  //---------------------------------------------------------------------

	  var QRMaskPattern = {
	    PATTERN000: 0,
	    PATTERN001: 1,
	    PATTERN010: 2,
	    PATTERN011: 3,
	    PATTERN100: 4,
	    PATTERN101: 5,
	    PATTERN110: 6,
	    PATTERN111: 7
	  };

	  //---------------------------------------------------------------------
	  // QRUtil
	  //---------------------------------------------------------------------

	  var QRUtil = function () {
	    var PATTERN_POSITION_TABLE = [[], [6, 18], [6, 22], [6, 26], [6, 30], [6, 34], [6, 22, 38], [6, 24, 42], [6, 26, 46], [6, 28, 50], [6, 30, 54], [6, 32, 58], [6, 34, 62], [6, 26, 46, 66], [6, 26, 48, 70], [6, 26, 50, 74], [6, 30, 54, 78], [6, 30, 56, 82], [6, 30, 58, 86], [6, 34, 62, 90], [6, 28, 50, 72, 94], [6, 26, 50, 74, 98], [6, 30, 54, 78, 102], [6, 28, 54, 80, 106], [6, 32, 58, 84, 110], [6, 30, 58, 86, 114], [6, 34, 62, 90, 118], [6, 26, 50, 74, 98, 122], [6, 30, 54, 78, 102, 126], [6, 26, 52, 78, 104, 130], [6, 30, 56, 82, 108, 134], [6, 34, 60, 86, 112, 138], [6, 30, 58, 86, 114, 142], [6, 34, 62, 90, 118, 146], [6, 30, 54, 78, 102, 126, 150], [6, 24, 50, 76, 102, 128, 154], [6, 28, 54, 80, 106, 132, 158], [6, 32, 58, 84, 110, 136, 162], [6, 26, 54, 82, 110, 138, 166], [6, 30, 58, 86, 114, 142, 170]];
	    var G15 = 1 << 10 | 1 << 8 | 1 << 5 | 1 << 4 | 1 << 2 | 1 << 1 | 1 << 0;
	    var G18 = 1 << 12 | 1 << 11 | 1 << 10 | 1 << 9 | 1 << 8 | 1 << 5 | 1 << 2 | 1 << 0;
	    var G15_MASK = 1 << 14 | 1 << 12 | 1 << 10 | 1 << 4 | 1 << 1;
	    var _this = {};
	    var getBCHDigit = function (data) {
	      var digit = 0;
	      while (data != 0) {
	        digit += 1;
	        data >>>= 1;
	      }
	      return digit;
	    };
	    _this.getBCHTypeInfo = function (data) {
	      var d = data << 10;
	      while (getBCHDigit(d) - getBCHDigit(G15) >= 0) {
	        d ^= G15 << getBCHDigit(d) - getBCHDigit(G15);
	      }
	      return (data << 10 | d) ^ G15_MASK;
	    };
	    _this.getBCHTypeNumber = function (data) {
	      var d = data << 12;
	      while (getBCHDigit(d) - getBCHDigit(G18) >= 0) {
	        d ^= G18 << getBCHDigit(d) - getBCHDigit(G18);
	      }
	      return data << 12 | d;
	    };
	    _this.getPatternPosition = function (typeNumber) {
	      return PATTERN_POSITION_TABLE[typeNumber - 1];
	    };
	    _this.getMaskFunction = function (maskPattern) {
	      switch (maskPattern) {
	        case QRMaskPattern.PATTERN000:
	          return function (i, j) {
	            return (i + j) % 2 == 0;
	          };
	        case QRMaskPattern.PATTERN001:
	          return function (i, j) {
	            return i % 2 == 0;
	          };
	        case QRMaskPattern.PATTERN010:
	          return function (i, j) {
	            return j % 3 == 0;
	          };
	        case QRMaskPattern.PATTERN011:
	          return function (i, j) {
	            return (i + j) % 3 == 0;
	          };
	        case QRMaskPattern.PATTERN100:
	          return function (i, j) {
	            return (Math.floor(i / 2) + Math.floor(j / 3)) % 2 == 0;
	          };
	        case QRMaskPattern.PATTERN101:
	          return function (i, j) {
	            return i * j % 2 + i * j % 3 == 0;
	          };
	        case QRMaskPattern.PATTERN110:
	          return function (i, j) {
	            return (i * j % 2 + i * j % 3) % 2 == 0;
	          };
	        case QRMaskPattern.PATTERN111:
	          return function (i, j) {
	            return (i * j % 3 + (i + j) % 2) % 2 == 0;
	          };
	        default:
	          throw 'bad maskPattern:' + maskPattern;
	      }
	    };
	    _this.getErrorCorrectPolynomial = function (errorCorrectLength) {
	      var a = qrPolynomial([1], 0);
	      for (var i = 0; i < errorCorrectLength; i += 1) {
	        a = a.multiply(qrPolynomial([1, QRMath.gexp(i)], 0));
	      }
	      return a;
	    };
	    _this.getLengthInBits = function (mode, type) {
	      if (1 <= type && type < 10) {
	        // 1 - 9

	        switch (mode) {
	          case QRMode.MODE_NUMBER:
	            return 10;
	          case QRMode.MODE_ALPHA_NUM:
	            return 9;
	          case QRMode.MODE_8BIT_BYTE:
	            return 8;
	          case QRMode.MODE_KANJI:
	            return 8;
	          default:
	            throw 'mode:' + mode;
	        }
	      } else if (type < 27) {
	        // 10 - 26

	        switch (mode) {
	          case QRMode.MODE_NUMBER:
	            return 12;
	          case QRMode.MODE_ALPHA_NUM:
	            return 11;
	          case QRMode.MODE_8BIT_BYTE:
	            return 16;
	          case QRMode.MODE_KANJI:
	            return 10;
	          default:
	            throw 'mode:' + mode;
	        }
	      } else if (type < 41) {
	        // 27 - 40

	        switch (mode) {
	          case QRMode.MODE_NUMBER:
	            return 14;
	          case QRMode.MODE_ALPHA_NUM:
	            return 13;
	          case QRMode.MODE_8BIT_BYTE:
	            return 16;
	          case QRMode.MODE_KANJI:
	            return 12;
	          default:
	            throw 'mode:' + mode;
	        }
	      } else {
	        throw 'type:' + type;
	      }
	    };
	    _this.getLostPoint = function (qrcode) {
	      var moduleCount = qrcode.getModuleCount();
	      var lostPoint = 0;

	      // LEVEL1

	      for (var row = 0; row < moduleCount; row += 1) {
	        for (var col = 0; col < moduleCount; col += 1) {
	          var sameCount = 0;
	          var dark = qrcode.isDark(row, col);
	          for (var r = -1; r <= 1; r += 1) {
	            if (row + r < 0 || moduleCount <= row + r) {
	              continue;
	            }
	            for (var c = -1; c <= 1; c += 1) {
	              if (col + c < 0 || moduleCount <= col + c) {
	                continue;
	              }
	              if (r == 0 && c == 0) {
	                continue;
	              }
	              if (dark == qrcode.isDark(row + r, col + c)) {
	                sameCount += 1;
	              }
	            }
	          }
	          if (sameCount > 5) {
	            lostPoint += 3 + sameCount - 5;
	          }
	        }
	      }

	      // LEVEL2

	      for (var row = 0; row < moduleCount - 1; row += 1) {
	        for (var col = 0; col < moduleCount - 1; col += 1) {
	          var count = 0;
	          if (qrcode.isDark(row, col)) count += 1;
	          if (qrcode.isDark(row + 1, col)) count += 1;
	          if (qrcode.isDark(row, col + 1)) count += 1;
	          if (qrcode.isDark(row + 1, col + 1)) count += 1;
	          if (count == 0 || count == 4) {
	            lostPoint += 3;
	          }
	        }
	      }

	      // LEVEL3

	      for (var row = 0; row < moduleCount; row += 1) {
	        for (var col = 0; col < moduleCount - 6; col += 1) {
	          if (qrcode.isDark(row, col) && !qrcode.isDark(row, col + 1) && qrcode.isDark(row, col + 2) && qrcode.isDark(row, col + 3) && qrcode.isDark(row, col + 4) && !qrcode.isDark(row, col + 5) && qrcode.isDark(row, col + 6)) {
	            lostPoint += 40;
	          }
	        }
	      }
	      for (var col = 0; col < moduleCount; col += 1) {
	        for (var row = 0; row < moduleCount - 6; row += 1) {
	          if (qrcode.isDark(row, col) && !qrcode.isDark(row + 1, col) && qrcode.isDark(row + 2, col) && qrcode.isDark(row + 3, col) && qrcode.isDark(row + 4, col) && !qrcode.isDark(row + 5, col) && qrcode.isDark(row + 6, col)) {
	            lostPoint += 40;
	          }
	        }
	      }

	      // LEVEL4

	      var darkCount = 0;
	      for (var col = 0; col < moduleCount; col += 1) {
	        for (var row = 0; row < moduleCount; row += 1) {
	          if (qrcode.isDark(row, col)) {
	            darkCount += 1;
	          }
	        }
	      }
	      var ratio = Math.abs(100 * darkCount / moduleCount / moduleCount - 50) / 5;
	      lostPoint += ratio * 10;
	      return lostPoint;
	    };
	    return _this;
	  }();

	  //---------------------------------------------------------------------
	  // QRMath
	  //---------------------------------------------------------------------

	  var QRMath = function () {
	    var EXP_TABLE = new Array(256);
	    var LOG_TABLE = new Array(256);

	    // initialize tables
	    for (var i = 0; i < 8; i += 1) {
	      EXP_TABLE[i] = 1 << i;
	    }
	    for (var i = 8; i < 256; i += 1) {
	      EXP_TABLE[i] = EXP_TABLE[i - 4] ^ EXP_TABLE[i - 5] ^ EXP_TABLE[i - 6] ^ EXP_TABLE[i - 8];
	    }
	    for (var i = 0; i < 255; i += 1) {
	      LOG_TABLE[EXP_TABLE[i]] = i;
	    }
	    var _this = {};
	    _this.glog = function (n) {
	      if (n < 1) {
	        throw 'glog(' + n + ')';
	      }
	      return LOG_TABLE[n];
	    };
	    _this.gexp = function (n) {
	      while (n < 0) {
	        n += 255;
	      }
	      while (n >= 256) {
	        n -= 255;
	      }
	      return EXP_TABLE[n];
	    };
	    return _this;
	  }();

	  //---------------------------------------------------------------------
	  // qrPolynomial
	  //---------------------------------------------------------------------

	  function qrPolynomial(num, shift) {
	    if (typeof num.length == 'undefined') {
	      throw num.length + '/' + shift;
	    }
	    var _num = function () {
	      var offset = 0;
	      while (offset < num.length && num[offset] == 0) {
	        offset += 1;
	      }
	      var _num = new Array(num.length - offset + shift);
	      for (var i = 0; i < num.length - offset; i += 1) {
	        _num[i] = num[i + offset];
	      }
	      return _num;
	    }();
	    var _this = {};
	    _this.getAt = function (index) {
	      return _num[index];
	    };
	    _this.getLength = function () {
	      return _num.length;
	    };
	    _this.multiply = function (e) {
	      var num = new Array(_this.getLength() + e.getLength() - 1);
	      for (var i = 0; i < _this.getLength(); i += 1) {
	        for (var j = 0; j < e.getLength(); j += 1) {
	          num[i + j] ^= QRMath.gexp(QRMath.glog(_this.getAt(i)) + QRMath.glog(e.getAt(j)));
	        }
	      }
	      return qrPolynomial(num, 0);
	    };
	    _this.mod = function (e) {
	      if (_this.getLength() - e.getLength() < 0) {
	        return _this;
	      }
	      var ratio = QRMath.glog(_this.getAt(0)) - QRMath.glog(e.getAt(0));
	      var num = new Array(_this.getLength());
	      for (var i = 0; i < _this.getLength(); i += 1) {
	        num[i] = _this.getAt(i);
	      }
	      for (var i = 0; i < e.getLength(); i += 1) {
	        num[i] ^= QRMath.gexp(QRMath.glog(e.getAt(i)) + ratio);
	      }

	      // recursive call
	      return qrPolynomial(num, 0).mod(e);
	    };
	    return _this;
	  }

	  //---------------------------------------------------------------------
	  // QRRSBlock
	  //---------------------------------------------------------------------

	  var QRRSBlock = function () {
	    var RS_BLOCK_TABLE = [
	    // L
	    // M
	    // Q
	    // H

	    // 1
	    [1, 26, 19], [1, 26, 16], [1, 26, 13], [1, 26, 9],
	    // 2
	    [1, 44, 34], [1, 44, 28], [1, 44, 22], [1, 44, 16],
	    // 3
	    [1, 70, 55], [1, 70, 44], [2, 35, 17], [2, 35, 13],
	    // 4
	    [1, 100, 80], [2, 50, 32], [2, 50, 24], [4, 25, 9],
	    // 5
	    [1, 134, 108], [2, 67, 43], [2, 33, 15, 2, 34, 16], [2, 33, 11, 2, 34, 12],
	    // 6
	    [2, 86, 68], [4, 43, 27], [4, 43, 19], [4, 43, 15],
	    // 7
	    [2, 98, 78], [4, 49, 31], [2, 32, 14, 4, 33, 15], [4, 39, 13, 1, 40, 14],
	    // 8
	    [2, 121, 97], [2, 60, 38, 2, 61, 39], [4, 40, 18, 2, 41, 19], [4, 40, 14, 2, 41, 15],
	    // 9
	    [2, 146, 116], [3, 58, 36, 2, 59, 37], [4, 36, 16, 4, 37, 17], [4, 36, 12, 4, 37, 13],
	    // 10
	    [2, 86, 68, 2, 87, 69], [4, 69, 43, 1, 70, 44], [6, 43, 19, 2, 44, 20], [6, 43, 15, 2, 44, 16],
	    // 11
	    [4, 101, 81], [1, 80, 50, 4, 81, 51], [4, 50, 22, 4, 51, 23], [3, 36, 12, 8, 37, 13],
	    // 12
	    [2, 116, 92, 2, 117, 93], [6, 58, 36, 2, 59, 37], [4, 46, 20, 6, 47, 21], [7, 42, 14, 4, 43, 15],
	    // 13
	    [4, 133, 107], [8, 59, 37, 1, 60, 38], [8, 44, 20, 4, 45, 21], [12, 33, 11, 4, 34, 12],
	    // 14
	    [3, 145, 115, 1, 146, 116], [4, 64, 40, 5, 65, 41], [11, 36, 16, 5, 37, 17], [11, 36, 12, 5, 37, 13],
	    // 15
	    [5, 109, 87, 1, 110, 88], [5, 65, 41, 5, 66, 42], [5, 54, 24, 7, 55, 25], [11, 36, 12, 7, 37, 13],
	    // 16
	    [5, 122, 98, 1, 123, 99], [7, 73, 45, 3, 74, 46], [15, 43, 19, 2, 44, 20], [3, 45, 15, 13, 46, 16],
	    // 17
	    [1, 135, 107, 5, 136, 108], [10, 74, 46, 1, 75, 47], [1, 50, 22, 15, 51, 23], [2, 42, 14, 17, 43, 15],
	    // 18
	    [5, 150, 120, 1, 151, 121], [9, 69, 43, 4, 70, 44], [17, 50, 22, 1, 51, 23], [2, 42, 14, 19, 43, 15],
	    // 19
	    [3, 141, 113, 4, 142, 114], [3, 70, 44, 11, 71, 45], [17, 47, 21, 4, 48, 22], [9, 39, 13, 16, 40, 14],
	    // 20
	    [3, 135, 107, 5, 136, 108], [3, 67, 41, 13, 68, 42], [15, 54, 24, 5, 55, 25], [15, 43, 15, 10, 44, 16],
	    // 21
	    [4, 144, 116, 4, 145, 117], [17, 68, 42], [17, 50, 22, 6, 51, 23], [19, 46, 16, 6, 47, 17],
	    // 22
	    [2, 139, 111, 7, 140, 112], [17, 74, 46], [7, 54, 24, 16, 55, 25], [34, 37, 13],
	    // 23
	    [4, 151, 121, 5, 152, 122], [4, 75, 47, 14, 76, 48], [11, 54, 24, 14, 55, 25], [16, 45, 15, 14, 46, 16],
	    // 24
	    [6, 147, 117, 4, 148, 118], [6, 73, 45, 14, 74, 46], [11, 54, 24, 16, 55, 25], [30, 46, 16, 2, 47, 17],
	    // 25
	    [8, 132, 106, 4, 133, 107], [8, 75, 47, 13, 76, 48], [7, 54, 24, 22, 55, 25], [22, 45, 15, 13, 46, 16],
	    // 26
	    [10, 142, 114, 2, 143, 115], [19, 74, 46, 4, 75, 47], [28, 50, 22, 6, 51, 23], [33, 46, 16, 4, 47, 17],
	    // 27
	    [8, 152, 122, 4, 153, 123], [22, 73, 45, 3, 74, 46], [8, 53, 23, 26, 54, 24], [12, 45, 15, 28, 46, 16],
	    // 28
	    [3, 147, 117, 10, 148, 118], [3, 73, 45, 23, 74, 46], [4, 54, 24, 31, 55, 25], [11, 45, 15, 31, 46, 16],
	    // 29
	    [7, 146, 116, 7, 147, 117], [21, 73, 45, 7, 74, 46], [1, 53, 23, 37, 54, 24], [19, 45, 15, 26, 46, 16],
	    // 30
	    [5, 145, 115, 10, 146, 116], [19, 75, 47, 10, 76, 48], [15, 54, 24, 25, 55, 25], [23, 45, 15, 25, 46, 16],
	    // 31
	    [13, 145, 115, 3, 146, 116], [2, 74, 46, 29, 75, 47], [42, 54, 24, 1, 55, 25], [23, 45, 15, 28, 46, 16],
	    // 32
	    [17, 145, 115], [10, 74, 46, 23, 75, 47], [10, 54, 24, 35, 55, 25], [19, 45, 15, 35, 46, 16],
	    // 33
	    [17, 145, 115, 1, 146, 116], [14, 74, 46, 21, 75, 47], [29, 54, 24, 19, 55, 25], [11, 45, 15, 46, 46, 16],
	    // 34
	    [13, 145, 115, 6, 146, 116], [14, 74, 46, 23, 75, 47], [44, 54, 24, 7, 55, 25], [59, 46, 16, 1, 47, 17],
	    // 35
	    [12, 151, 121, 7, 152, 122], [12, 75, 47, 26, 76, 48], [39, 54, 24, 14, 55, 25], [22, 45, 15, 41, 46, 16],
	    // 36
	    [6, 151, 121, 14, 152, 122], [6, 75, 47, 34, 76, 48], [46, 54, 24, 10, 55, 25], [2, 45, 15, 64, 46, 16],
	    // 37
	    [17, 152, 122, 4, 153, 123], [29, 74, 46, 14, 75, 47], [49, 54, 24, 10, 55, 25], [24, 45, 15, 46, 46, 16],
	    // 38
	    [4, 152, 122, 18, 153, 123], [13, 74, 46, 32, 75, 47], [48, 54, 24, 14, 55, 25], [42, 45, 15, 32, 46, 16],
	    // 39
	    [20, 147, 117, 4, 148, 118], [40, 75, 47, 7, 76, 48], [43, 54, 24, 22, 55, 25], [10, 45, 15, 67, 46, 16],
	    // 40
	    [19, 148, 118, 6, 149, 119], [18, 75, 47, 31, 76, 48], [34, 54, 24, 34, 55, 25], [20, 45, 15, 61, 46, 16]];
	    var qrRSBlock = function (totalCount, dataCount) {
	      var _this = {};
	      _this.totalCount = totalCount;
	      _this.dataCount = dataCount;
	      return _this;
	    };
	    var _this = {};
	    var getRsBlockTable = function (typeNumber, errorCorrectionLevel) {
	      switch (errorCorrectionLevel) {
	        case QRErrorCorrectionLevel.L:
	          return RS_BLOCK_TABLE[(typeNumber - 1) * 4 + 0];
	        case QRErrorCorrectionLevel.M:
	          return RS_BLOCK_TABLE[(typeNumber - 1) * 4 + 1];
	        case QRErrorCorrectionLevel.Q:
	          return RS_BLOCK_TABLE[(typeNumber - 1) * 4 + 2];
	        case QRErrorCorrectionLevel.H:
	          return RS_BLOCK_TABLE[(typeNumber - 1) * 4 + 3];
	        default:
	          return undefined;
	      }
	    };
	    _this.getRSBlocks = function (typeNumber, errorCorrectionLevel) {
	      var rsBlock = getRsBlockTable(typeNumber, errorCorrectionLevel);
	      if (typeof rsBlock == 'undefined') {
	        throw 'bad rs block @ typeNumber:' + typeNumber + '/errorCorrectionLevel:' + errorCorrectionLevel;
	      }
	      var length = rsBlock.length / 3;
	      var list = [];
	      for (var i = 0; i < length; i += 1) {
	        var count = rsBlock[i * 3 + 0];
	        var totalCount = rsBlock[i * 3 + 1];
	        var dataCount = rsBlock[i * 3 + 2];
	        for (var j = 0; j < count; j += 1) {
	          list.push(qrRSBlock(totalCount, dataCount));
	        }
	      }
	      return list;
	    };
	    return _this;
	  }();

	  //---------------------------------------------------------------------
	  // qrBitBuffer
	  //---------------------------------------------------------------------

	  var qrBitBuffer = function () {
	    var _buffer = [];
	    var _length = 0;
	    var _this = {};
	    _this.getBuffer = function () {
	      return _buffer;
	    };
	    _this.getAt = function (index) {
	      var bufIndex = Math.floor(index / 8);
	      return (_buffer[bufIndex] >>> 7 - index % 8 & 1) == 1;
	    };
	    _this.put = function (num, length) {
	      for (var i = 0; i < length; i += 1) {
	        _this.putBit((num >>> length - i - 1 & 1) == 1);
	      }
	    };
	    _this.getLengthInBits = function () {
	      return _length;
	    };
	    _this.putBit = function (bit) {
	      var bufIndex = Math.floor(_length / 8);
	      if (_buffer.length <= bufIndex) {
	        _buffer.push(0);
	      }
	      if (bit) {
	        _buffer[bufIndex] |= 0x80 >>> _length % 8;
	      }
	      _length += 1;
	    };
	    return _this;
	  };

	  //---------------------------------------------------------------------
	  // qrNumber
	  //---------------------------------------------------------------------

	  var qrNumber = function (data) {
	    var _mode = QRMode.MODE_NUMBER;
	    var _data = data;
	    var _this = {};
	    _this.getMode = function () {
	      return _mode;
	    };
	    _this.getLength = function (buffer) {
	      return _data.length;
	    };
	    _this.write = function (buffer) {
	      var data = _data;
	      var i = 0;
	      while (i + 2 < data.length) {
	        buffer.put(strToNum(data.substring(i, i + 3)), 10);
	        i += 3;
	      }
	      if (i < data.length) {
	        if (data.length - i == 1) {
	          buffer.put(strToNum(data.substring(i, i + 1)), 4);
	        } else if (data.length - i == 2) {
	          buffer.put(strToNum(data.substring(i, i + 2)), 7);
	        }
	      }
	    };
	    var strToNum = function (s) {
	      var num = 0;
	      for (var i = 0; i < s.length; i += 1) {
	        num = num * 10 + chatToNum(s.charAt(i));
	      }
	      return num;
	    };
	    var chatToNum = function (c) {
	      if ('0' <= c && c <= '9') {
	        return c.charCodeAt(0) - '0'.charCodeAt(0);
	      }
	      throw 'illegal char :' + c;
	    };
	    return _this;
	  };

	  //---------------------------------------------------------------------
	  // qrAlphaNum
	  //---------------------------------------------------------------------

	  var qrAlphaNum = function (data) {
	    var _mode = QRMode.MODE_ALPHA_NUM;
	    var _data = data;
	    var _this = {};
	    _this.getMode = function () {
	      return _mode;
	    };
	    _this.getLength = function (buffer) {
	      return _data.length;
	    };
	    _this.write = function (buffer) {
	      var s = _data;
	      var i = 0;
	      while (i + 1 < s.length) {
	        buffer.put(getCode(s.charAt(i)) * 45 + getCode(s.charAt(i + 1)), 11);
	        i += 2;
	      }
	      if (i < s.length) {
	        buffer.put(getCode(s.charAt(i)), 6);
	      }
	    };
	    var getCode = function (c) {
	      if ('0' <= c && c <= '9') {
	        return c.charCodeAt(0) - '0'.charCodeAt(0);
	      } else if ('A' <= c && c <= 'Z') {
	        return c.charCodeAt(0) - 'A'.charCodeAt(0) + 10;
	      } else {
	        switch (c) {
	          case ' ':
	            return 36;
	          case '$':
	            return 37;
	          case '%':
	            return 38;
	          case '*':
	            return 39;
	          case '+':
	            return 40;
	          case '-':
	            return 41;
	          case '.':
	            return 42;
	          case '/':
	            return 43;
	          case ':':
	            return 44;
	          default:
	            throw 'illegal char :' + c;
	        }
	      }
	    };
	    return _this;
	  };

	  //---------------------------------------------------------------------
	  // qr8BitByte
	  //---------------------------------------------------------------------

	  var qr8BitByte = function (data) {
	    var _mode = QRMode.MODE_8BIT_BYTE;
	    var _bytes = qrcode.stringToBytes(data);
	    var _this = {};
	    _this.getMode = function () {
	      return _mode;
	    };
	    _this.getLength = function (buffer) {
	      return _bytes.length;
	    };
	    _this.write = function (buffer) {
	      for (var i = 0; i < _bytes.length; i += 1) {
	        buffer.put(_bytes[i], 8);
	      }
	    };
	    return _this;
	  };

	  //---------------------------------------------------------------------
	  // qrKanji
	  //---------------------------------------------------------------------

	  var qrKanji = function (data) {
	    var _mode = QRMode.MODE_KANJI;
	    var stringToBytes = qrcode.stringToBytesFuncs['SJIS'];
	    if (!stringToBytes) {
	      throw 'sjis not supported.';
	    }
	    !function (c, code) {
	      // self test for sjis support.
	      var test = stringToBytes(c);
	      if (test.length != 2 || (test[0] << 8 | test[1]) != code) {
	        throw 'sjis not supported.';
	      }
	    }('\u53cb', 0x9746);
	    var _bytes = stringToBytes(data);
	    var _this = {};
	    _this.getMode = function () {
	      return _mode;
	    };
	    _this.getLength = function (buffer) {
	      return ~~(_bytes.length / 2);
	    };
	    _this.write = function (buffer) {
	      var data = _bytes;
	      var i = 0;
	      while (i + 1 < data.length) {
	        var c = (0xff & data[i]) << 8 | 0xff & data[i + 1];
	        if (0x8140 <= c && c <= 0x9FFC) {
	          c -= 0x8140;
	        } else if (0xE040 <= c && c <= 0xEBBF) {
	          c -= 0xC140;
	        } else {
	          throw 'illegal char at ' + (i + 1) + '/' + c;
	        }
	        c = (c >>> 8 & 0xff) * 0xC0 + (c & 0xff);
	        buffer.put(c, 13);
	        i += 2;
	      }
	      if (i < data.length) {
	        throw 'illegal char at ' + (i + 1);
	      }
	    };
	    return _this;
	  };

	  //=====================================================================
	  // GIF Support etc.
	  //

	  //---------------------------------------------------------------------
	  // byteArrayOutputStream
	  //---------------------------------------------------------------------

	  var byteArrayOutputStream = function () {
	    var _bytes = [];
	    var _this = {};
	    _this.writeByte = function (b) {
	      _bytes.push(b & 0xff);
	    };
	    _this.writeShort = function (i) {
	      _this.writeByte(i);
	      _this.writeByte(i >>> 8);
	    };
	    _this.writeBytes = function (b, off, len) {
	      off = off || 0;
	      len = len || b.length;
	      for (var i = 0; i < len; i += 1) {
	        _this.writeByte(b[i + off]);
	      }
	    };
	    _this.writeString = function (s) {
	      for (var i = 0; i < s.length; i += 1) {
	        _this.writeByte(s.charCodeAt(i));
	      }
	    };
	    _this.toByteArray = function () {
	      return _bytes;
	    };
	    _this.toString = function () {
	      var s = '';
	      s += '[';
	      for (var i = 0; i < _bytes.length; i += 1) {
	        if (i > 0) {
	          s += ',';
	        }
	        s += _bytes[i];
	      }
	      s += ']';
	      return s;
	    };
	    return _this;
	  };

	  //---------------------------------------------------------------------
	  // base64EncodeOutputStream
	  //---------------------------------------------------------------------

	  var base64EncodeOutputStream = function () {
	    var _buffer = 0;
	    var _buflen = 0;
	    var _length = 0;
	    var _base64 = '';
	    var _this = {};
	    var writeEncoded = function (b) {
	      _base64 += String.fromCharCode(encode(b & 0x3f));
	    };
	    var encode = function (n) {
	      if (n < 0) ; else if (n < 26) {
	        return 0x41 + n;
	      } else if (n < 52) {
	        return 0x61 + (n - 26);
	      } else if (n < 62) {
	        return 0x30 + (n - 52);
	      } else if (n == 62) {
	        return 0x2b;
	      } else if (n == 63) {
	        return 0x2f;
	      }
	      throw 'n:' + n;
	    };
	    _this.writeByte = function (n) {
	      _buffer = _buffer << 8 | n & 0xff;
	      _buflen += 8;
	      _length += 1;
	      while (_buflen >= 6) {
	        writeEncoded(_buffer >>> _buflen - 6);
	        _buflen -= 6;
	      }
	    };
	    _this.flush = function () {
	      if (_buflen > 0) {
	        writeEncoded(_buffer << 6 - _buflen);
	        _buffer = 0;
	        _buflen = 0;
	      }
	      if (_length % 3 != 0) {
	        // padding
	        var padlen = 3 - _length % 3;
	        for (var i = 0; i < padlen; i += 1) {
	          _base64 += '=';
	        }
	      }
	    };
	    _this.toString = function () {
	      return _base64;
	    };
	    return _this;
	  };

	  //---------------------------------------------------------------------
	  // base64DecodeInputStream
	  //---------------------------------------------------------------------

	  var base64DecodeInputStream = function (str) {
	    var _str = str;
	    var _pos = 0;
	    var _buffer = 0;
	    var _buflen = 0;
	    var _this = {};
	    _this.read = function () {
	      while (_buflen < 8) {
	        if (_pos >= _str.length) {
	          if (_buflen == 0) {
	            return -1;
	          }
	          throw 'unexpected end of file./' + _buflen;
	        }
	        var c = _str.charAt(_pos);
	        _pos += 1;
	        if (c == '=') {
	          _buflen = 0;
	          return -1;
	        } else if (c.match(/^\s$/)) {
	          // ignore if whitespace.
	          continue;
	        }
	        _buffer = _buffer << 6 | decode(c.charCodeAt(0));
	        _buflen += 6;
	      }
	      var n = _buffer >>> _buflen - 8 & 0xff;
	      _buflen -= 8;
	      return n;
	    };
	    var decode = function (c) {
	      if (0x41 <= c && c <= 0x5a) {
	        return c - 0x41;
	      } else if (0x61 <= c && c <= 0x7a) {
	        return c - 0x61 + 26;
	      } else if (0x30 <= c && c <= 0x39) {
	        return c - 0x30 + 52;
	      } else if (c == 0x2b) {
	        return 62;
	      } else if (c == 0x2f) {
	        return 63;
	      } else {
	        throw 'c:' + c;
	      }
	    };
	    return _this;
	  };

	  //---------------------------------------------------------------------
	  // gifImage (B/W)
	  //---------------------------------------------------------------------

	  var gifImage = function (width, height) {
	    var _width = width;
	    var _height = height;
	    var _data = new Array(width * height);
	    var _this = {};
	    _this.setPixel = function (x, y, pixel) {
	      _data[y * _width + x] = pixel;
	    };
	    _this.write = function (out) {
	      //---------------------------------
	      // GIF Signature

	      out.writeString('GIF87a');

	      //---------------------------------
	      // Screen Descriptor

	      out.writeShort(_width);
	      out.writeShort(_height);
	      out.writeByte(0x80); // 2bit
	      out.writeByte(0);
	      out.writeByte(0);

	      //---------------------------------
	      // Global Color Map

	      // black
	      out.writeByte(0x00);
	      out.writeByte(0x00);
	      out.writeByte(0x00);

	      // white
	      out.writeByte(0xff);
	      out.writeByte(0xff);
	      out.writeByte(0xff);

	      //---------------------------------
	      // Image Descriptor

	      out.writeString(',');
	      out.writeShort(0);
	      out.writeShort(0);
	      out.writeShort(_width);
	      out.writeShort(_height);
	      out.writeByte(0);

	      //---------------------------------
	      // Local Color Map

	      //---------------------------------
	      // Raster Data

	      var lzwMinCodeSize = 2;
	      var raster = getLZWRaster(lzwMinCodeSize);
	      out.writeByte(lzwMinCodeSize);
	      var offset = 0;
	      while (raster.length - offset > 255) {
	        out.writeByte(255);
	        out.writeBytes(raster, offset, 255);
	        offset += 255;
	      }
	      out.writeByte(raster.length - offset);
	      out.writeBytes(raster, offset, raster.length - offset);
	      out.writeByte(0x00);

	      //---------------------------------
	      // GIF Terminator
	      out.writeString(';');
	    };
	    var bitOutputStream = function (out) {
	      var _out = out;
	      var _bitLength = 0;
	      var _bitBuffer = 0;
	      var _this = {};
	      _this.write = function (data, length) {
	        if (data >>> length != 0) {
	          throw 'length over';
	        }
	        while (_bitLength + length >= 8) {
	          _out.writeByte(0xff & (data << _bitLength | _bitBuffer));
	          length -= 8 - _bitLength;
	          data >>>= 8 - _bitLength;
	          _bitBuffer = 0;
	          _bitLength = 0;
	        }
	        _bitBuffer = data << _bitLength | _bitBuffer;
	        _bitLength = _bitLength + length;
	      };
	      _this.flush = function () {
	        if (_bitLength > 0) {
	          _out.writeByte(_bitBuffer);
	        }
	      };
	      return _this;
	    };
	    var getLZWRaster = function (lzwMinCodeSize) {
	      var clearCode = 1 << lzwMinCodeSize;
	      var endCode = (1 << lzwMinCodeSize) + 1;
	      var bitLength = lzwMinCodeSize + 1;

	      // Setup LZWTable
	      var table = lzwTable();
	      for (var i = 0; i < clearCode; i += 1) {
	        table.add(String.fromCharCode(i));
	      }
	      table.add(String.fromCharCode(clearCode));
	      table.add(String.fromCharCode(endCode));
	      var byteOut = byteArrayOutputStream();
	      var bitOut = bitOutputStream(byteOut);

	      // clear code
	      bitOut.write(clearCode, bitLength);
	      var dataIndex = 0;
	      var s = String.fromCharCode(_data[dataIndex]);
	      dataIndex += 1;
	      while (dataIndex < _data.length) {
	        var c = String.fromCharCode(_data[dataIndex]);
	        dataIndex += 1;
	        if (table.contains(s + c)) {
	          s = s + c;
	        } else {
	          bitOut.write(table.indexOf(s), bitLength);
	          if (table.size() < 0xfff) {
	            if (table.size() == 1 << bitLength) {
	              bitLength += 1;
	            }
	            table.add(s + c);
	          }
	          s = c;
	        }
	      }
	      bitOut.write(table.indexOf(s), bitLength);

	      // end code
	      bitOut.write(endCode, bitLength);
	      bitOut.flush();
	      return byteOut.toByteArray();
	    };
	    var lzwTable = function () {
	      var _map = {};
	      var _size = 0;
	      var _this = {};
	      _this.add = function (key) {
	        if (_this.contains(key)) {
	          throw 'dup key:' + key;
	        }
	        _map[key] = _size;
	        _size += 1;
	      };
	      _this.size = function () {
	        return _size;
	      };
	      _this.indexOf = function (key) {
	        return _map[key];
	      };
	      _this.contains = function (key) {
	        return typeof _map[key] != 'undefined';
	      };
	      return _this;
	    };
	    return _this;
	  };
	  var createDataURL = function (width, height, getPixel) {
	    var gif = gifImage(width, height);
	    for (var y = 0; y < height; y += 1) {
	      for (var x = 0; x < width; x += 1) {
	        gif.setPixel(x, y, getPixel(x, y));
	      }
	    }
	    var b = byteArrayOutputStream();
	    gif.write(b);
	    var base64 = base64EncodeOutputStream();
	    var bytes = b.toByteArray();
	    for (var i = 0; i < bytes.length; i += 1) {
	      base64.writeByte(bytes[i]);
	    }
	    base64.flush();
	    return 'data:image/gif;base64,' + base64;
	  };

	  //---------------------------------------------------------------------
	  // returns qrcode function.

	  return qrcode;
	}();

	// multibyte support
	!function () {
	  qrcode.stringToBytesFuncs['UTF-8'] = function (s) {
	    // http://stackoverflow.com/questions/18729405/how-to-convert-utf8-string-to-byte-array
	    function toUTF8Array(str) {
	      var utf8 = [];
	      for (var i = 0; i < str.length; i++) {
	        var charcode = str.charCodeAt(i);
	        if (charcode < 0x80) utf8.push(charcode);else if (charcode < 0x800) {
	          utf8.push(0xc0 | charcode >> 6, 0x80 | charcode & 0x3f);
	        } else if (charcode < 0xd800 || charcode >= 0xe000) {
	          utf8.push(0xe0 | charcode >> 12, 0x80 | charcode >> 6 & 0x3f, 0x80 | charcode & 0x3f);
	        }
	        // surrogate pair
	        else {
	          i++;
	          // UTF-16 encodes 0x10000-0x10FFFF by
	          // subtracting 0x10000 and splitting the
	          // 20 bits of 0x0-0xFFFFF into two halves
	          charcode = 0x10000 + ((charcode & 0x3ff) << 10 | str.charCodeAt(i) & 0x3ff);
	          utf8.push(0xf0 | charcode >> 18, 0x80 | charcode >> 12 & 0x3f, 0x80 | charcode >> 6 & 0x3f, 0x80 | charcode & 0x3f);
	        }
	      }
	      return utf8;
	    }
	    return toUTF8Array(s);
	  };
	}();
	(function (factory) {
	  {
	    module.exports = factory();
	  }
	})(function () {
	  return qrcode;
	}); 
} (qrcode$1));

var qrcodeExports = qrcode$1.exports;
var qrcode = /*@__PURE__*/getDefaultExportFromCjs(qrcodeExports);

const _tmpl$$j = /*#__PURE__*/template(`<svg version="1.1" xmlns="http://www.w3.org/2000/svg" preserveAspectRatio="xMinYMin meet"><path stroke="transparent" fill="currentColor">`);
function QrCode(props) {
  const qrCodeRef = createMemo(() => {
    const codeRef = qrcode(0, "L");
    codeRef.addData(props.data);
    codeRef.make();
    return codeRef;
  });
  const qrCodeSize = () => qrCodeRef().getModuleCount();
  const qrCodeSvgPathData = createMemo(() => [...Array(qrCodeSize()).keys()].flatMap(row => [...Array(qrCodeSize()).keys()].filter(column => qrCodeRef().isDark(row, column)).map(column => `M${column},${row}l1,0 0,1 -1,0,0,-1z`)).join(" "));
  return (() => {
    const _el$ = _tmpl$$j(),
      _el$2 = _el$.firstChild;
    createRenderEffect(_p$ => {
      const _v$ = `0 0 ${qrCodeSize()} ${qrCodeSize()}`,
        _v$2 = props.class,
        _v$3 = props.style,
        _v$4 = qrCodeSvgPathData();
      _v$ !== _p$._v$ && setAttribute(_el$, "viewBox", _p$._v$ = _v$);
      _v$2 !== _p$._v$2 && setAttribute(_el$, "class", _p$._v$2 = _v$2);
      _p$._v$3 = style(_el$, _v$3, _p$._v$3);
      _v$4 !== _p$._v$4 && setAttribute(_el$2, "d", _p$._v$4 = _v$4);
      return _p$;
    }, {
      _v$: undefined,
      _v$2: undefined,
      _v$3: undefined,
      _v$4: undefined
    });
    return _el$;
  })();
}

const isMobile = "userAgentData" in navigator ? navigator["userAgentData"].mobile : false;

var styles$9 = {"qrCode":"UsernamelessLogIn-module_qrCode__TNZaO","errorStateQrCode":"UsernamelessLogIn-module_errorStateQrCode__GYNE5","qrCodeLoader":"UsernamelessLogIn-module_qrCodeLoader__16MS7","appLink":"UsernamelessLogIn-module_appLink__N0eiR","appLinkLoader":"UsernamelessLogIn-module_appLinkLoader__6w7zF","verificationCode":"UsernamelessLogIn-module_verificationCode__zKsZS","verificationCodeValue":"UsernamelessLogIn-module_verificationCodeValue__TWSXa","error":"UsernamelessLogIn-module_error__NP9Wh","retryButton":"UsernamelessLogIn-module_retryButton__lsg4P"};

var formStyles = {"form":"Form-module_form__CrKt3","formDivider":"Form-module_formDivider__y-h8c","methodTitle":"Form-module_methodTitle__qsX7L","methodDescription":"Form-module_methodDescription__04vxV","methodForm":"Form-module_methodForm__BAMSB","methodLink":"Form-module_methodLink__wa7fA"};

const _tmpl$$i = /*#__PURE__*/template(`<p>`),
  _tmpl$2$8 = /*#__PURE__*/template(`<button>`),
  _tmpl$3$5 = /*#__PURE__*/template(`<div><p></p><p>`),
  _tmpl$4 = /*#__PURE__*/template(`<span>`);
function UsernamelessLogIn(props) {
  const tt = useTranslationTable();
  const isError = () => props.error !== undefined && props.error !== null;
  return (() => {
    const _el$ = _tmpl$3$5(),
      _el$2 = _el$.firstChild,
      _el$3 = _el$2.nextSibling;
    insert(_el$2, () => tt().form.actions.logIn.usernameless.title);
    insert(_el$3, () => tt().form.actions.logIn.usernameless.description[isMobile ? "appLink" : "qrCode"]);
    insert(_el$, createComponent(Show, {
      when: isMobile,
      get fallback() {
        return createComponent(Show, {
          get when() {
            return props.qrCodeData !== null;
          },
          get fallback() {
            return createComponent(QrCodeLoader, {
              get isActive() {
                return !isError();
              },
              get ["class"]() {
                return styles$9.qrCodeLoader;
              }
            });
          },
          get children() {
            return createComponent(QrCode, {
              get data() {
                return props.qrCodeData;
              },
              get ["class"]() {
                return clsx(styles$9.qrCode, {
                  [styles$9.errorStateQrCode]: isError()
                });
              }
            });
          }
        });
      },
      get children() {
        return createComponent(Show, {
          get when() {
            return props.qrCodeData !== null;
          },
          get fallback() {
            return createComponent(AppLinkLoader, {
              get isActive() {
                return !isError();
              },
              get ["class"]() {
                return styles$9.appLinkLoader;
              }
            });
          },
          get children() {
            return createComponent(AppLink, {
              get data() {
                return props.qrCodeData;
              },
              get ["class"]() {
                return styles$9.appLink;
              }
            });
          }
        });
      }
    }), null);
    insert(_el$, createComponent(Show, {
      get when() {
        return props.verificationCode !== null;
      },
      get children() {
        const _el$4 = _tmpl$$i();
        insert(_el$4, () => tt().form.actions.logIn.usernameless.verificationCode((() => {
          const _el$7 = _tmpl$4();
          insert(_el$7, () => props.verificationCode);
          createRenderEffect(() => className(_el$7, styles$9.verificationCodeValue));
          return _el$7;
        })()));
        createRenderEffect(() => className(_el$4, styles$9.verificationCode));
        return _el$4;
      }
    }), null);
    insert(_el$, createComponent(Show, {
      get when() {
        return isError();
      },
      get children() {
        return [(() => {
          const _el$5 = _tmpl$$i();
          insert(_el$5, () => tt().form.errors.usernamelessLogIn[props.error]);
          createRenderEffect(() => className(_el$5, styles$9.error));
          return _el$5;
        })(), (() => {
          const _el$6 = _tmpl$2$8();
          _el$6.$$click = () => props.onRetry();
          insert(_el$6, () => tt().form.actions.logIn.usernameless.retry);
          createRenderEffect(() => className(_el$6, styles$9.retryButton));
          return _el$6;
        })()];
      }
    }), null);
    createRenderEffect(_p$ => {
      const _v$ = props.class,
        _v$2 = props.style,
        _v$3 = formStyles.methodTitle,
        _v$4 = formStyles.methodDescription;
      _v$ !== _p$._v$ && className(_el$, _p$._v$ = _v$);
      _p$._v$2 = style(_el$, _v$2, _p$._v$2);
      _v$3 !== _p$._v$3 && className(_el$2, _p$._v$3 = _v$3);
      _v$4 !== _p$._v$4 && className(_el$3, _p$._v$4 = _v$4);
      return _p$;
    }, {
      _v$: undefined,
      _v$2: undefined,
      _v$3: undefined,
      _v$4: undefined
    });
    return _el$;
  })();
}
delegateEvents(["click"]);

var styles$8 = {"label":"FormField-module_label__HJec-","control":"FormField-module_control__E0Nuq","error":"FormField-module_error__u6tnS"};

const _tmpl$$h = /*#__PURE__*/template(`<p>`),
  _tmpl$2$7 = /*#__PURE__*/template(`<div><p></p><div>`);
function FormField(props) {
  return (() => {
    const _el$ = _tmpl$2$7(),
      _el$2 = _el$.firstChild,
      _el$3 = _el$2.nextSibling;
    insert(_el$2, () => props.label);
    insert(_el$3, () => props.children);
    insert(_el$, createComponent(Show, {
      get when() {
        return props.error !== undefined && props.error !== null;
      },
      get children() {
        const _el$4 = _tmpl$$h();
        insert(_el$4, () => props.error);
        createRenderEffect(() => className(_el$4, styles$8.error));
        return _el$4;
      }
    }), null);
    createRenderEffect(_p$ => {
      const _v$ = clsx(styles$8.field, props.class),
        _v$2 = props.style,
        _v$3 = styles$8.label,
        _v$4 = styles$8.control;
      _v$ !== _p$._v$ && className(_el$, _p$._v$ = _v$);
      _p$._v$2 = style(_el$, _v$2, _p$._v$2);
      _v$3 !== _p$._v$3 && className(_el$2, _p$._v$3 = _v$3);
      _v$4 !== _p$._v$4 && className(_el$3, _p$._v$4 = _v$4);
      return _p$;
    }, {
      _v$: undefined,
      _v$2: undefined,
      _v$3: undefined,
      _v$4: undefined
    });
    return _el$;
  })();
}

var styles$7 = {"input":"InputFormControl-module_input__JsAnk"};

var formControlStyles = {"control":"FormControl-module_control__bCKwa"};

const _tmpl$$g = /*#__PURE__*/template(`<input>`);
function InputFormControl(_props) {
  const [props, passthroughProps] = splitProps(_props, ["class"]);
  return (() => {
    const _el$ = _tmpl$$g();
    spread(_el$, mergeProps({
      get ["class"]() {
        return clsx(formControlStyles.control, styles$7.input, props.class);
      }
    }, passthroughProps), false, false);
    return _el$;
  })();
}

var styles$6 = {"button":"ButtonFormControl-module_button__x4Pq9"};

const _tmpl$$f = /*#__PURE__*/template(`<button>`);
function ButtonFormControl(_props) {
  const [props, passthroughProps] = splitProps(_props, ["class"]);
  return (() => {
    const _el$ = _tmpl$$f();
    spread(_el$, mergeProps({
      get ["class"]() {
        return clsx(formControlStyles.control, styles$6.button, props.class);
      }
    }, passthroughProps), false, false);
    return _el$;
  })();
}

const _tmpl$$e = /*#__PURE__*/template(`<div><p></p><form>`);
function UsernameLogIn(props) {
  const tt = useTranslationTable();
  const errorString = () => props.error !== undefined && props.error !== null ? tt().form.errors.usernameLogIn[props.error] : null;
  let inputRef = null;
  const handleSubmit = event => {
    event.preventDefault();
    const username = inputRef.value;
    props.onRequest(username);
  };
  return (() => {
    const _el$ = _tmpl$$e(),
      _el$2 = _el$.firstChild,
      _el$3 = _el$2.nextSibling;
    insert(_el$2, () => tt().form.actions.logIn.username.title);
    _el$3.addEventListener("submit", handleSubmit);
    insert(_el$3, createComponent(FormField, {
      get label() {
        return tt().form.actions.logIn.username.fields.username.label;
      },
      get error() {
        return errorString();
      },
      get children() {
        return createComponent(InputFormControl, {
          ref(r$) {
            const _ref$ = inputRef;
            typeof _ref$ === "function" ? _ref$(r$) : inputRef = r$;
          },
          type: "text"
        });
      }
    }), null);
    insert(_el$3, createComponent(ButtonFormControl, {
      type: "submit",
      get disabled() {
        return props.isLoading;
      },
      get children() {
        return tt().form.actions.logIn.username.submit;
      }
    }), null);
    createRenderEffect(_p$ => {
      const _v$ = props.class,
        _v$2 = props.style,
        _v$3 = formStyles.methodTitle,
        _v$4 = formStyles.methodForm;
      _v$ !== _p$._v$ && className(_el$, _p$._v$ = _v$);
      _p$._v$2 = style(_el$, _v$2, _p$._v$2);
      _v$3 !== _p$._v$3 && className(_el$2, _p$._v$3 = _v$3);
      _v$4 !== _p$._v$4 && className(_el$3, _p$._v$4 = _v$4);
      return _p$;
    }, {
      _v$: undefined,
      _v$2: undefined,
      _v$3: undefined,
      _v$4: undefined
    });
    return _el$;
  })();
}

const _tmpl$$d = /*#__PURE__*/template(`<span>`),
  _tmpl$2$6 = /*#__PURE__*/template(`<button>`),
  _tmpl$3$4 = /*#__PURE__*/template(`<div>`);
function LogInForm(props) {
  const tt = useTranslationTable();
  const isUsernamelessLogInAvailable = () => props.qrCodeData !== false;
  const [isUserSelectedUsernameForm, setIsUserSelectedUsernameForm] = createSignal(false);
  const isUsernameForm = () => isUsernamelessLogInAvailable() ? isUserSelectedUsernameForm() : true;
  const toggleUserSelectedUsernameForm = () => {
    setIsUserSelectedUsernameForm(isUserSelectedUsernameForm => !isUserSelectedUsernameForm);
  };
  return (() => {
    const _el$ = _tmpl$3$4();
    insert(_el$, createComponent(Show, {
      get when() {
        return isUsernameForm();
      },
      get fallback() {
        return createComponent(UsernamelessLogIn, {
          get qrCodeData() {
            return props.qrCodeData;
          },
          get verificationCode() {
            return props.verificationCode ?? null;
          },
          get onRetry() {
            return props.onUsernamelessRetry;
          },
          get error() {
            return props.usernamelessError;
          }
        });
      },
      get children() {
        return createComponent(UsernameLogIn, {
          get isLoading() {
            return props.isLoading;
          },
          get error() {
            return props.usernameError;
          },
          get onRequest() {
            return props.onUsernameLogInRequest;
          }
        });
      }
    }), null);
    insert(_el$, createComponent(Show, {
      get when() {
        return isUsernamelessLogInAvailable();
      },
      get children() {
        return [(() => {
          const _el$2 = _tmpl$$d();
          createRenderEffect(() => className(_el$2, formStyles.formDivider));
          return _el$2;
        })(), (() => {
          const _el$3 = _tmpl$2$6();
          _el$3.$$click = toggleUserSelectedUsernameForm;
          insert(_el$3, () => tt().form.actions.logIn.methodButtons[isUsernameForm() ? "usernameless" : "username"]);
          createRenderEffect(() => className(_el$3, formStyles.methodLink));
          return _el$3;
        })()];
      }
    }), null);
    createRenderEffect(() => className(_el$, formStyles.form));
    return _el$;
  })();
}
delegateEvents(["click"]);

const _tmpl$$c = /*#__PURE__*/template(`<div><p></p><form>`);
function UsernameRegister(props) {
  const tt = useTranslationTable();
  const errorString = () => props.error !== undefined && props.error !== null ? tt().form.errors.usernameRegister[props.error] : null;
  let inputRef = null;
  const handleSubmit = event => {
    event.preventDefault();
    const username = inputRef.value;
    props.onRequest(username);
  };
  return (() => {
    const _el$ = _tmpl$$c(),
      _el$2 = _el$.firstChild,
      _el$3 = _el$2.nextSibling;
    insert(_el$2, () => tt().form.actions.register.username.title);
    _el$3.addEventListener("submit", handleSubmit);
    insert(_el$3, createComponent(FormField, {
      get label() {
        return tt().form.actions.register.username.fields.username.label;
      },
      get error() {
        return errorString();
      },
      get children() {
        return createComponent(InputFormControl, {
          ref(r$) {
            const _ref$ = inputRef;
            typeof _ref$ === "function" ? _ref$(r$) : inputRef = r$;
          },
          type: "text"
        });
      }
    }), null);
    insert(_el$3, createComponent(ButtonFormControl, {
      type: "submit",
      get disabled() {
        return props.isLoading;
      },
      get children() {
        return tt().form.actions.register.username.submit;
      }
    }), null);
    createRenderEffect(_p$ => {
      const _v$ = props.class,
        _v$2 = props.style,
        _v$3 = formStyles.methodTitle,
        _v$4 = formStyles.methodForm;
      _v$ !== _p$._v$ && className(_el$, _p$._v$ = _v$);
      _p$._v$2 = style(_el$, _v$2, _p$._v$2);
      _v$3 !== _p$._v$3 && className(_el$2, _p$._v$3 = _v$3);
      _v$4 !== _p$._v$4 && className(_el$3, _p$._v$4 = _v$4);
      return _p$;
    }, {
      _v$: undefined,
      _v$2: undefined,
      _v$3: undefined,
      _v$4: undefined
    });
    return _el$;
  })();
}

const _tmpl$$b = /*#__PURE__*/template(`<div>`);
function RegisterForm(props) {
  return (() => {
    const _el$ = _tmpl$$b();
    insert(_el$, createComponent(UsernameRegister, {
      get isLoading() {
        return props.isLoading;
      },
      get error() {
        return props.usernameError;
      },
      get onRequest() {
        return props.onUsernameRegisterRequest;
      }
    }));
    createRenderEffect(() => className(_el$, formStyles.form));
    return _el$;
  })();
}

var styles$5 = {"card":"OptionCard-module_card__M8GfS","icon":"OptionCard-module_icon__DQ13B","label":"OptionCard-module_label__RwFmz","description":"OptionCard-module_description__VLgLU"};

const _tmpl$$a = /*#__PURE__*/template(`<p>`),
  _tmpl$2$5 = /*#__PURE__*/template(`<button><img><p>`);
function OptionCard(props) {
  return (() => {
    const _el$ = _tmpl$2$5(),
      _el$2 = _el$.firstChild,
      _el$3 = _el$2.nextSibling;
    _el$.$$click = () => props.onClick?.();
    const _ref$ = props.ref;
    typeof _ref$ === "function" ? use(_ref$, _el$) : props.ref = _el$;
    insert(_el$3, () => props.label);
    insert(_el$, createComponent(Show, {
      get when() {
        return props.description !== undefined;
      },
      get children() {
        const _el$4 = _tmpl$$a();
        insert(_el$4, () => props.description);
        createRenderEffect(() => className(_el$4, styles$5.description));
        return _el$4;
      }
    }), null);
    createRenderEffect(_p$ => {
      const _v$ = clsx(styles$5.card, props.class),
        _v$2 = props.style,
        _v$3 = props.iconSrc,
        _v$4 = props.label,
        _v$5 = styles$5.icon,
        _v$6 = styles$5.label;
      _v$ !== _p$._v$ && className(_el$, _p$._v$ = _v$);
      _p$._v$2 = style(_el$, _v$2, _p$._v$2);
      _v$3 !== _p$._v$3 && setAttribute(_el$2, "src", _p$._v$3 = _v$3);
      _v$4 !== _p$._v$4 && setAttribute(_el$2, "alt", _p$._v$4 = _v$4);
      _v$5 !== _p$._v$5 && className(_el$2, _p$._v$5 = _v$5);
      _v$6 !== _p$._v$6 && className(_el$3, _p$._v$6 = _v$6);
      return _p$;
    }, {
      _v$: undefined,
      _v$2: undefined,
      _v$3: undefined,
      _v$4: undefined,
      _v$5: undefined,
      _v$6: undefined
    });
    return _el$;
  })();
}
delegateEvents(["click"]);

var styles$4 = {"options":"MethodSelectionPrompt-module_options__NeS-p"};

var promptStyles = {"prompt":"Prompt-module_prompt__vnPmJ","promptDescription":"Prompt-module_promptDescription__G542l","loader":"Prompt-module_loader__3jfDR","promptAction":"Prompt-module_promptAction__chNUL","info":"Prompt-module_info__-m7DY","error":"Prompt-module_error__PqlmU","errorButton":"Prompt-module_errorButton__GbBXK"};

var img$2 = "data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' class='feather feather-mail'%3e%3cpath d='M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z'%3e%3c/path%3e%3cpolyline points='22%2c6 12%2c13 2%2c6'%3e%3c/polyline%3e%3c/svg%3e";

var img$1 = "data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' class='feather feather-globe'%3e%3ccircle cx='12' cy='12' r='10'%3e%3c/circle%3e%3cline x1='2' y1='12' x2='22' y2='12'%3e%3c/line%3e%3cpath d='M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z'%3e%3c/path%3e%3c/svg%3e";

const _tmpl$$9 = /*#__PURE__*/template(`<div><p></p><div>`);
function MethodSelectionPrompt(props) {
  const tt = useTranslationTable();
  return (() => {
    const _el$ = _tmpl$$9(),
      _el$2 = _el$.firstChild,
      _el$3 = _el$2.nextSibling;
    insert(_el$2, () => tt().form.methods.prompt);
    insert(_el$3, createComponent(Show, {
      get when() {
        return props.availableMethods.authenticator;
      },
      get children() {
        return createComponent(OptionCard, {
          iconSrc: img$3,
          get label() {
            return tt().form.methods.authenticator.label;
          },
          get description() {
            return tt().form.methods.authenticator.description;
          },
          onClick: () => props.onSelect("authenticator")
        });
      }
    }), null);
    insert(_el$3, createComponent(Show, {
      get when() {
        return props.availableMethods.magicLinkEmail;
      },
      get children() {
        return createComponent(OptionCard, {
          iconSrc: img$2,
          get label() {
            return tt().form.methods.magicLinkEmail.label;
          },
          get description() {
            return tt().form.methods.magicLinkEmail.description;
          },
          onClick: () => props.onSelect("magicLinkEmail")
        });
      }
    }), null);
    insert(_el$3, createComponent(Show, {
      get when() {
        return props.availableMethods.passkey;
      },
      get children() {
        return createComponent(OptionCard, {
          iconSrc: img$1,
          get label() {
            return tt().form.methods.passkey.label;
          },
          get description() {
            return tt().form.methods.passkey.description;
          },
          onClick: () => props.onSelect("passkey")
        });
      }
    }), null);
    createRenderEffect(_p$ => {
      const _v$ = props.class,
        _v$2 = props.style,
        _v$3 = promptStyles.prompt,
        _v$4 = clsx(promptStyles.promptAction, styles$4.options);
      _v$ !== _p$._v$ && className(_el$, _p$._v$ = _v$);
      _p$._v$2 = style(_el$, _v$2, _p$._v$2);
      _v$3 !== _p$._v$3 && className(_el$2, _p$._v$3 = _v$3);
      _v$4 !== _p$._v$4 && className(_el$3, _p$._v$4 = _v$4);
      return _p$;
    }, {
      _v$: undefined,
      _v$2: undefined,
      _v$3: undefined,
      _v$4: undefined
    });
    return _el$;
  })();
}

var styles$3 = {"controlsBar":"PromptContainer-module_controlsBar__rjg-X","backButton":"PromptContainer-module_backButton__IXkwB","backButtonIcon":"PromptContainer-module_backButtonIcon__t2lI3","promptPurpose":"PromptContainer-module_promptPurpose__aDQO4","promptPurposeDetail":"PromptContainer-module_promptPurposeDetail__CC99N","prompt":"PromptContainer-module_prompt__7uWuc"};

var img = "data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' class='feather feather-chevron-left'%3e%3cpolyline points='15 18 9 12 15 6'%3e%3c/polyline%3e%3c/svg%3e";

const _tmpl$$8 = /*#__PURE__*/template(`<button><img>`),
  _tmpl$2$4 = /*#__PURE__*/template(`<div><div><p></p></div><div>`),
  _tmpl$3$3 = /*#__PURE__*/template(`<span>`);
function PromptContainer(props) {
  const tt = useTranslationTable();
  return (() => {
    const _el$ = _tmpl$2$4(),
      _el$2 = _el$.firstChild,
      _el$5 = _el$2.firstChild,
      _el$6 = _el$2.nextSibling;
    insert(_el$2, createComponent(Show, {
      get when() {
        return props.onGoBack !== undefined;
      },
      get children() {
        const _el$3 = _tmpl$$8(),
          _el$4 = _el$3.firstChild;
        _el$3.$$click = () => props.onGoBack();
        setAttribute(_el$4, "src", img);
        createRenderEffect(_p$ => {
          const _v$ = styles$3.backButton,
            _v$2 = tt().form.back,
            _v$3 = styles$3.backButtonIcon;
          _v$ !== _p$._v$ && className(_el$3, _p$._v$ = _v$);
          _v$2 !== _p$._v$2 && setAttribute(_el$4, "alt", _p$._v$2 = _v$2);
          _v$3 !== _p$._v$3 && className(_el$4, _p$._v$3 = _v$3);
          return _p$;
        }, {
          _v$: undefined,
          _v$2: undefined,
          _v$3: undefined
        });
        return _el$3;
      }
    }), _el$5);
    insert(_el$5, () => (props.isRegistering ? tt().form.actions.register.prompt : tt().form.actions.logIn.prompt)((() => {
      const _el$7 = _tmpl$3$3();
      insert(_el$7, () => props.username);
      createRenderEffect(() => className(_el$7, styles$3.promptPurposeDetail));
      return _el$7;
    })()));
    insert(_el$6, () => props.children);
    createRenderEffect(_p$ => {
      const _v$4 = props.class,
        _v$5 = props.style,
        _v$6 = styles$3.controlsBar,
        _v$7 = styles$3.promptPurpose,
        _v$8 = styles$3.prompt;
      _v$4 !== _p$._v$4 && className(_el$, _p$._v$4 = _v$4);
      _p$._v$5 = style(_el$, _v$5, _p$._v$5);
      _v$6 !== _p$._v$6 && className(_el$2, _p$._v$6 = _v$6);
      _v$7 !== _p$._v$7 && className(_el$5, _p$._v$7 = _v$7);
      _v$8 !== _p$._v$8 && className(_el$6, _p$._v$8 = _v$8);
      return _p$;
    }, {
      _v$4: undefined,
      _v$5: undefined,
      _v$6: undefined,
      _v$7: undefined,
      _v$8: undefined
    });
    return _el$;
  })();
}
delegateEvents(["click"]);

var styles$2 = {"qrCode":"AuthenticatorPrompt-module_qrCode__fTNln","errorStateQrCode":"AuthenticatorPrompt-module_errorStateQrCode__Qkp4r","appLink":"AuthenticatorPrompt-module_appLink__exUqJ"};

const _tmpl$$7 = /*#__PURE__*/template(`<p>`),
  _tmpl$2$3 = /*#__PURE__*/template(`<button>`),
  _tmpl$3$2 = /*#__PURE__*/template(`<div><p></p><p></p><div>`);
function AuthenticatorPrompt(props) {
  const tt = useTranslationTable();
  const isError = () => props.error !== undefined && props.error !== null;
  return (() => {
    const _el$ = _tmpl$3$2(),
      _el$2 = _el$.firstChild,
      _el$3 = _el$2.nextSibling,
      _el$4 = _el$3.nextSibling;
    insert(_el$2, () => (props.isRegistering ? tt().form.prompts.authenticator.register : tt().form.prompts.authenticator.logIn).title);
    insert(_el$3, () => tt().form.prompts.authenticator[props.isRegistering ? "register" : "logIn"].description[isMobile ? "appLink" : "qrCode"]);
    insert(_el$4, createComponent(Show, {
      get when() {
        return props.qrCodeData !== null;
      },
      get fallback() {
        return createComponent(QrCodeLoader, {
          get isActive() {
            return !isError();
          },
          get ["class"]() {
            return styles$2.qrCode;
          }
        });
      },
      get children() {
        return createComponent(Show, {
          when: isMobile,
          get fallback() {
            return createComponent(QrCode, {
              get data() {
                return props.qrCodeData;
              },
              get ["class"]() {
                return clsx(styles$2.qrCode, {
                  [styles$2.errorStateQrCode]: isError()
                });
              }
            });
          },
          get children() {
            return createComponent(AppLink, {
              get data() {
                return props.qrCodeData;
              },
              get ["class"]() {
                return styles$2.appLink;
              }
            });
          }
        });
      }
    }));
    insert(_el$, createComponent(Show, {
      get when() {
        return props.error !== undefined && props.error !== null;
      },
      get children() {
        return [(() => {
          const _el$5 = _tmpl$$7();
          insert(_el$5, () => tt().form.errors.authenticator[props.error]);
          createRenderEffect(() => className(_el$5, promptStyles.error));
          return _el$5;
        })(), createComponent(Show, {
          get when() {
            return props.onErrorRecoverRequest !== undefined;
          },
          get children() {
            const _el$6 = _tmpl$2$3();
            _el$6.$$click = () => props.onErrorRecoverRequest();
            insert(_el$6, () => tt().form.errors.recover);
            createRenderEffect(() => className(_el$6, promptStyles.errorButton));
            return _el$6;
          }
        })];
      }
    }), null);
    createRenderEffect(_p$ => {
      const _v$ = props.class,
        _v$2 = props.style,
        _v$3 = promptStyles.prompt,
        _v$4 = promptStyles.promptDescription,
        _v$5 = clsx(promptStyles.promptAction, styles$2.qrCode);
      _v$ !== _p$._v$ && className(_el$, _p$._v$ = _v$);
      _p$._v$2 = style(_el$, _v$2, _p$._v$2);
      _v$3 !== _p$._v$3 && className(_el$2, _p$._v$3 = _v$3);
      _v$4 !== _p$._v$4 && className(_el$3, _p$._v$4 = _v$4);
      _v$5 !== _p$._v$5 && className(_el$4, _p$._v$5 = _v$5);
      return _p$;
    }, {
      _v$: undefined,
      _v$2: undefined,
      _v$3: undefined,
      _v$4: undefined,
      _v$5: undefined
    });
    return _el$;
  })();
}
delegateEvents(["click"]);

const _tmpl$$6 = /*#__PURE__*/template(`<p>`),
  _tmpl$2$2 = /*#__PURE__*/template(`<button>`),
  _tmpl$3$1 = /*#__PURE__*/template(`<div><p></p><p>`);
function MagicLinkEmailPrompt(props) {
  const tt = useTranslationTable();
  return (() => {
    const _el$ = _tmpl$3$1(),
      _el$2 = _el$.firstChild,
      _el$3 = _el$2.nextSibling;
    insert(_el$2, () => tt().form.prompts.magicLinkEmail[props.isRegistering ? "register" : "logIn"].title);
    insert(_el$3, () => tt().form.prompts.magicLinkEmail[props.isRegistering ? "register" : "logIn"].description);
    insert(_el$, createComponent(Show, {
      get when() {
        return props.isOutOfBandCompleted;
      },
      get children() {
        const _el$4 = _tmpl$$6();
        insert(_el$4, () => tt().form.prompts.magicLinkEmail[props.isRegistering ? "register" : "logIn"].outOfBandCompletionInfo);
        createRenderEffect(() => className(_el$4, promptStyles.info));
        return _el$4;
      }
    }), null);
    insert(_el$, createComponent(Show, {
      get when() {
        return props.error !== undefined && props.error !== null;
      },
      get children() {
        return [(() => {
          const _el$5 = _tmpl$$6();
          insert(_el$5, () => tt().form.errors.magicLinkEmail[props.error]);
          createRenderEffect(() => className(_el$5, promptStyles.error));
          return _el$5;
        })(), createComponent(Show, {
          get when() {
            return props.onErrorRecoverRequest !== undefined;
          },
          get children() {
            const _el$6 = _tmpl$2$2();
            _el$6.$$click = () => props.onErrorRecoverRequest();
            insert(_el$6, () => tt().form.errors.recover);
            createRenderEffect(() => className(_el$6, promptStyles.errorButton));
            return _el$6;
          }
        })];
      }
    }), null);
    createRenderEffect(_p$ => {
      const _v$ = props.class,
        _v$2 = props.style,
        _v$3 = promptStyles.prompt,
        _v$4 = promptStyles.promptDescription;
      _v$ !== _p$._v$ && className(_el$, _p$._v$ = _v$);
      _p$._v$2 = style(_el$, _v$2, _p$._v$2);
      _v$3 !== _p$._v$3 && className(_el$2, _p$._v$3 = _v$3);
      _v$4 !== _p$._v$4 && className(_el$3, _p$._v$4 = _v$4);
      return _p$;
    }, {
      _v$: undefined,
      _v$2: undefined,
      _v$3: undefined,
      _v$4: undefined
    });
    return _el$;
  })();
}
delegateEvents(["click"]);

var styles$1 = {"progressLoader":"ProgressLoader-module_progressLoader__2E6vy","activeProgressLoader":"ProgressLoader-module_activeProgressLoader__-nino","lds-grid":"ProgressLoader-module_lds-grid__2hQDt"};

const _tmpl$$5 = /*#__PURE__*/template(`<div>`);
function ProgressLoader(props) {
  return (() => {
    const _el$ = _tmpl$$5();
    insert(_el$, () => [...new Array(3)].map(() => _tmpl$$5()));
    createRenderEffect(_p$ => {
      const _v$ = clsx(styles$1.progressLoader, {
          [styles$1.activeProgressLoader]: props.isActive
        }, props.class),
        _v$2 = props.style;
      _v$ !== _p$._v$ && className(_el$, _p$._v$ = _v$);
      _p$._v$2 = style(_el$, _v$2, _p$._v$2);
      return _p$;
    }, {
      _v$: undefined,
      _v$2: undefined
    });
    return _el$;
  })();
}

const _tmpl$$4 = /*#__PURE__*/template(`<p>`),
  _tmpl$2$1 = /*#__PURE__*/template(`<button>`),
  _tmpl$3 = /*#__PURE__*/template(`<div><p></p><p>`);
function PasskeyPrompt(props) {
  const tt = useTranslationTable();
  const isError = () => props.error !== undefined && props.error !== null;
  return (() => {
    const _el$ = _tmpl$3(),
      _el$2 = _el$.firstChild,
      _el$3 = _el$2.nextSibling;
    insert(_el$2, () => (props.isRegistering ? tt().form.prompts.passkey.register : tt().form.prompts.passkey.logIn).title);
    insert(_el$3, () => (props.isRegistering ? tt().form.prompts.passkey.register : tt().form.prompts.passkey.logIn).description);
    insert(_el$, createComponent(ProgressLoader, {
      get isActive() {
        return !isError();
      },
      get ["class"]() {
        return promptStyles.loader;
      }
    }), null);
    insert(_el$, createComponent(Show, {
      get when() {
        return isError();
      },
      get children() {
        return [(() => {
          const _el$4 = _tmpl$$4();
          insert(_el$4, () => tt().form.errors.passkey[props.error]);
          createRenderEffect(() => className(_el$4, promptStyles.error));
          return _el$4;
        })(), createComponent(Show, {
          get when() {
            return props.onErrorRecoverRequest !== undefined;
          },
          get children() {
            const _el$5 = _tmpl$2$1();
            _el$5.$$click = () => props.onErrorRecoverRequest();
            insert(_el$5, () => tt().form.errors.recover);
            createRenderEffect(() => className(_el$5, promptStyles.errorButton));
            return _el$5;
          }
        })];
      }
    }), null);
    createRenderEffect(_p$ => {
      const _v$ = props.class,
        _v$2 = props.style,
        _v$3 = promptStyles.prompt,
        _v$4 = promptStyles.promptDescription;
      _v$ !== _p$._v$ && className(_el$, _p$._v$ = _v$);
      _p$._v$2 = style(_el$, _v$2, _p$._v$2);
      _v$3 !== _p$._v$3 && className(_el$2, _p$._v$3 = _v$3);
      _v$4 !== _p$._v$4 && className(_el$3, _p$._v$4 = _v$4);
      return _p$;
    }, {
      _v$: undefined,
      _v$2: undefined,
      _v$3: undefined,
      _v$4: undefined
    });
    return _el$;
  })();
}
delegateEvents(["click"]);

const _tmpl$$3 = /*#__PURE__*/template(`<div><p></p><p>`);
function WaitPrompt(props) {
  const tt = useTranslationTable();
  return (() => {
    const _el$ = _tmpl$$3(),
      _el$2 = _el$.firstChild,
      _el$3 = _el$2.nextSibling;
    insert(_el$2, () => tt().form.prompts.wait.title);
    insert(_el$3, () => tt().form.prompts.wait.description);
    insert(_el$, createComponent(ProgressLoader, {
      isActive: true,
      get ["class"]() {
        return promptStyles.loader;
      }
    }), null);
    createRenderEffect(_p$ => {
      const _v$ = props.class,
        _v$2 = props.style,
        _v$3 = promptStyles.prompt,
        _v$4 = promptStyles.promptDescription;
      _v$ !== _p$._v$ && className(_el$, _p$._v$ = _v$);
      _p$._v$2 = style(_el$, _v$2, _p$._v$2);
      _v$3 !== _p$._v$3 && className(_el$2, _p$._v$3 = _v$3);
      _v$4 !== _p$._v$4 && className(_el$3, _p$._v$4 = _v$4);
      return _p$;
    }, {
      _v$: undefined,
      _v$2: undefined,
      _v$3: undefined,
      _v$4: undefined
    });
    return _el$;
  })();
}

const _tmpl$$2 = /*#__PURE__*/template(`<div><p></p><p></p><div>`);
function CaptchaPrompt(props) {
  const tt = useTranslationTable();
  return (() => {
    const _el$ = _tmpl$$2(),
      _el$2 = _el$.firstChild,
      _el$3 = _el$2.nextSibling,
      _el$4 = _el$3.nextSibling;
    insert(_el$2, () => tt().form.prompts.captcha.title);
    insert(_el$3, () => tt().form.prompts.captcha.description);
    insert(_el$4, () => props.captcha);
    createRenderEffect(_p$ => {
      const _v$ = props.class,
        _v$2 = props.style,
        _v$3 = promptStyles.prompt,
        _v$4 = promptStyles.promptDescription,
        _v$5 = promptStyles.promptAction;
      _v$ !== _p$._v$ && className(_el$, _p$._v$ = _v$);
      _p$._v$2 = style(_el$, _v$2, _p$._v$2);
      _v$3 !== _p$._v$3 && className(_el$2, _p$._v$3 = _v$3);
      _v$4 !== _p$._v$4 && className(_el$3, _p$._v$4 = _v$4);
      _v$5 !== _p$._v$5 && className(_el$4, _p$._v$5 = _v$5);
      return _p$;
    }, {
      _v$: undefined,
      _v$2: undefined,
      _v$3: undefined,
      _v$4: undefined,
      _v$5: undefined
    });
    return _el$;
  })();
}

const _tmpl$$1 = /*#__PURE__*/template(`<div><div>`),
  _tmpl$2 = /*#__PURE__*/template(`<div>`);
function CaptchaProtectedWindow(props) {
  let captchaRef = null;
  let captchaId = null;
  const [isCaptchaLoaded, setIsCaptchaLoaded] = createSignal(typeof hcaptcha !== "undefined");
  const [isCaptchaActive, setIsCaptchaActive] = createSignal(false);
  createEffect(() => {
    if (props.hCaptchaSiteId !== null && props.isCaptchaRequired && !isCaptchaLoaded()) {
      const script = document.createElement("script");
      script.src = "https://js.hcaptcha.com/1/api.js";
      script.async = true;
      script.defer = true;
      script.onload = () => setIsCaptchaLoaded(true);
      document.head.appendChild(script);
    }
  });
  const handleCaptchaSubmission = () => {
    const captchaConfirmation = {
      hCaptchaResponse: hcaptcha.getResponse(captchaId),
      hCaptchaResponseKey: hcaptcha.getRespKey(captchaId)
    };
    setIsCaptchaActive(false);
    props.onConfirm(captchaConfirmation);
  };
  const handleCaptchaOpen = () => {
    setIsCaptchaActive(true);
  };
  createEffect(() => {
    if (!isCaptchaLoaded() || !props.isCaptchaRequired) {
      return;
    }
    const _captchaId = hcaptcha.render(captchaRef, {
      sitekey: props.hCaptchaSiteId,
      callback: handleCaptchaSubmission,
      "open-callback": handleCaptchaOpen
    });
    captchaId = _captchaId;
    hcaptcha.execute(captchaId);
    onCleanup(() => {
      hcaptcha.reset(_captchaId);
      hcaptcha.remove(_captchaId);
    });
  });
  return (() => {
    const _el$ = _tmpl$$1(),
      _el$2 = _el$.firstChild;
    insert(_el$, createComponent(CaptchaPrompt, {
      get style() {
        return {
          display: isCaptchaActive() ? undefined : "none"
        };
      },
      get captcha() {
        return (() => {
          const _el$3 = _tmpl$2();
          const _ref$ = captchaRef;
          typeof _ref$ === "function" ? use(_ref$, _el$3) : captchaRef = _el$3;
          return _el$3;
        })();
      }
    }), _el$2);
    insert(_el$2, () => props.children);
    createRenderEffect(() => (isCaptchaActive() ? "none" : undefined) != null ? _el$2.style.setProperty("display", isCaptchaActive() ? "none" : undefined) : _el$2.style.removeProperty("display"));
    return _el$;
  })();
}

function useDocumentVisibility() {
  const [isVisible, setIsVisible] = createSignal(!document.hidden);
  const updateVisibility = () => setIsVisible(!document.hidden);
  document.addEventListener("visibilitychange", updateVisibility);
  onCleanup(() => {
    document.removeEventListener("visibilitychange", updateVisibility);
  });
  return isVisible;
}

const defaultUiOptions = {
  appLinkBackgroundColor: "hsl(0deg 0% 95%)",
  appLinkIconForegroundColor: "hsl(0deg 0% 15%)",
  appLinkLabelForegroundColor: "hsl(0deg 0% 15%)",
  qrCodeForegroundColor: "hsl(0deg 0% 15%)",
  qrCodeErrorForegroundColor: "hsl(0deg 0% 75%)",
  verificationCodeForegroundColor: "hsl(0deg 0% 30%)",
  verificationCodeValueForegroundColor: "hsl(290deg 100% 40%)",
  formDividerColor: "hsl(0deg 0% 90%)",
  formMethodTitleForegroundColor: "hsl(0deg 0% 15%)",
  formMethodDescriptionForegroundColor: "hsl(0deg 0% 30%)",
  formMethodLinkForegroundColor: "hsl(0deg 0% 30%)",
  promptForegroundColor: "hsl(0deg 0% 15%)",
  promptDescriptionForegroundColor: "hsl(0deg 0% 30%)",
  promptInfoForegroundColor: "hsl(140deg 100% 40%)",
  promptErrorForegroundColor: "hsl(0deg 100% 40%)",
  formButtonBackgroundColor: "hsl(290deg 100% 40%)",
  formButtonForegroundColor: "hsl(0deg 0% 100%)",
  formButtonHoverBackgroundColor: "hsl(290deg 100% 60%)",
  formButtonHoverForegroundColor: "hsl(0deg 0% 100%)",
  formButtonDisabledBackgroundColor: "hsl(0deg 0% 45%)",
  formButtonDisabledForegroundColor: "hsl(0deg 0% 90%)",
  formFieldLabelForegroundColor: "hsl(0deg 0% 15%)",
  formFieldErrorForegroundColor: "hsl(0deg 100% 40%)",
  formInputBackgroundColor: "hsl(0deg 0% 95%)",
  formInputForegroundColor: "hsl(0deg 0% 15%)",
  optionCardBackgroundColor: "hsl(0deg 0% 95%)",
  optionCardHoverBackgroundColor: "hsl(290deg 50% 95%)",
  optionCardIconForegroundColor: "hsl(0deg 0% 15%)",
  optionCardLabelForegroundColor: "hsl(0deg 0% 15%)",
  optionCardDescriptionForegroundColor: "hsl(0deg 0% 30%)",
  qrCodeLoaderColor: "hsl(0deg 0% 75%)",
  qrCodeLoaderActiveColor1: "hsl(0deg 0% 85%)",
  qrCodeLoaderActiveColor2: "hsl(0deg 0% 15%)",
  progressLoaderColor: "hsl(0deg 0% 75%)",
  progressLoaderActiveColor1: "hsl(0deg 0% 85%)",
  progressLoaderActiveColor2: "hsl(290deg 100% 80%)",
  tabForegroundColor: "hsl(0deg 0% 45%)",
  tabHoverForegroundColor: "hsl(0deg 0% 15%)",
  tabActiveForegroundColor: "hsl(290deg 100% 40%)"
};

class NoAuthenticationMethodsAvailableError extends Error {
  constructor() {
    super("No methods are available for this user.");
  }
}

class UserAlreadyExistsError extends Error {
  constructor() {
    super("The specified user already exists.");
  }
}

class UserNotFoundError extends Error {
  constructor() {
    super("The specified user was not found.");
  }
}

var styles = {"authArmorForm":"AuthArmorForm-module_authArmorForm__3bP5t","view":"AuthArmorForm-module_view__Apg-3"};

const _tmpl$ = /*#__PURE__*/template(`<section><div>`);
const defaultFormAction = "logIn";
function AuthArmorForm(props) {
  const tt = () => props.interactiveConfig.internationalizationOptions?.translationTable ?? defaultTranslationTable;
  const [usernamelessLogInError, setUsernamelessLogInError] = createSignal(null);
  const [usernameLogInError, setUsernameLogInError] = createSignal(null);
  const [usernameRegisterError, setUsernameRegisterError] = createSignal(null);
  const [authenticatorError, setAuthenticatorError] = createSignal(null);
  const [magicLinkEmailError, setMagicLinkEmailError] = createSignal(null);
  const [passkeyError, setPasskeyError] = createSignal(null);
  const [userSelectedCurrentAction, setUserSelectedCurrentAction] = createSignal(null);
  const currentAction = createMemo(() => props.action ?? userSelectedCurrentAction() ?? props.defaultAction ?? defaultFormAction);
  const [userSelectedCurrentUsername, setUserSelectedCurrentUsername] = createSignal(null);
  const currentUsername = createMemo(() => props.username ?? userSelectedCurrentUsername());
  const [availableMethods, setAvailableMethods] = createSignal(null);
  const availableMethodsList = createMemo(() => availableMethods() && Object.entries(availableMethods()).filter(([_, isEnabled]) => isEnabled).map(([method]) => method));
  const singleAvailableMethod = () => availableMethodsList()?.length === 1 ? availableMethodsList()[0] : null;
  const [userSelectedCurrentMethod, setUserSelectedCurrentMethod] = createSignal(null);
  const currentMethod = createMemo(() => props.method ?? singleAvailableMethod() ?? userSelectedCurrentMethod());
  const enableUsernamelessLogIn = () => props.enableUsernamelessLogIn && props.username === null;
  const [usernamelessLogInQrCodeData, setUsernamelessLogInQrCodeData] = createSignal(null);
  const [usernamelessLogInVerificationCode, setUsernamelessLogInVerificationCode] = createSignal();
  const [qrCodeData, setQrCodeData] = createSignal(null);
  const [hCaptchaSiteId, setHCaptchaSiteId] = createSignal(null);
  const [captchaConfirmation, setCaptchaConfirmation] = createSignal(null);
  const [isCaptchaConfirmationStale, setIsCaptchaConfirmationStale] = createSignal(false);
  const isCaptchaRequired = () => currentMethod() === "authenticator" && currentAction() === "logIn" || currentMethod() === "magicLinkEmail";
  const isCaptchaPending = () => isCaptchaRequired() && hCaptchaSiteId() !== false && captchaConfirmation() === null;
  const [isLoading, setIsLoading] = createSignal(false);
  const [isSucceeded, setIsSucceeded] = createSignal(false);
  const [isOutOfBandCompleted, setIsOutOfBandCompleted] = createSignal(false);
  const isDocumentVisible = useDocumentVisibility();
  const isDesktopDocumentVisible = createMemo(() => isMobile ? true : isDocumentVisible());
  const uiOptions = createMemo(() => ({
    ...defaultUiOptions,
    ...props.interactiveConfig.uiOptions
  }));
  const uiOptionsDerivedStyles = createMemo(() => Object.fromEntries(Object.entries(uiOptions()).map(([property, value]) => [`--${property.replace(/([a-z])([A-Z])/g, "$1-$2").replace(/([a-zA-Z])([0-9])/g, "$1-$2").replace(/([0-9])(a-zA-Z)/g, "$1-$2").toLowerCase()}`, value])));
  createEffect(on(() => props.client, client => {
    let isCleanedUp = false;
    onCleanup(() => {
      setHCaptchaSiteId(null);
      isCleanedUp = true;
    });
    client.getHCaptchaSiteId().then(hCaptchaSiteId => {
      if (isCleanedUp) return;
      setHCaptchaSiteId(hCaptchaSiteId ?? false);
    });
  }));
  createEffect(on([enableUsernamelessLogIn, currentAction, currentUsername, availableMethods, isDesktopDocumentVisible, usernamelessLogInError], async ([enableUsernamelessLogIn, action, username, availableMethods, isDesktopDocumentVisible, usernamelessLogInError]) => {
    if (!enableUsernamelessLogIn || action !== "logIn" || username !== null && availableMethods !== null || !isDesktopDocumentVisible || usernamelessLogInError !== null) {
      return;
    }
    setUsernamelessLogInQrCodeData(null);
    setUsernamelessLogInVerificationCode(null);
    const abortController = new AbortController();
    onCleanup(() => {
      setUsernamelessLogInQrCodeData(null);
      setUsernamelessLogInVerificationCode(null);
      abortController.abort();
    });
    while (!abortController.signal.aborted) {
      let qrResult;
      try {
        qrResult = await props.client.authenticateWithAuthenticatorUsernamelessAsync({
          ...props.interactiveConfig.defaultAuthenticateOptions,
          ...props.interactiveConfig.defaultAuthenticatorAuthenticateOptions,
          ...props.interactiveConfig.defaultAuthenticatorUsernamelessAuthenticateOptions
        }, abortController.signal);
      } catch (error) {
        if (abortController.signal.aborted) {
          return;
        }
        if (error instanceof TypeError) {
          setUsernamelessLogInError("network");
        } else {
          setUsernamelessLogInError("unknown");
        }
        props.onError(error);
        return;
      }
      if (abortController.signal.aborted) {
        return;
      }
      setUsernamelessLogInQrCodeData(qrResult.qrCodeUrl);
      setUsernamelessLogInVerificationCode(qrResult.verificationCode);
      const authenticationResult = await qrResult.resultAsync();
      if (authenticationResult.succeeded) {
        setIsSucceeded(true);
        setUserSelectedCurrentUsername(authenticationResult.username);
        props.onLogIn(authenticationResult);
        break;
      } else if (authenticationResult.failureReason !== "timedOut") {
        switch (authenticationResult.failureReason) {
          case "declined":
            {
              setUsernamelessLogInError("declined");
              break;
            }
          default:
            {
              setUsernamelessLogInError("unknown");
              break;
            }
        }
        props.onLogInFailure(authenticationResult);
        break;
      }
    }
  }));
  createEffect(on([currentUsername], async ([username]) => {
    setAvailableMethods(null);
    if (username === null) return;
    const abortController = new AbortController();
    onCleanup(() => {
      setIsLoading(false);
      abortController.abort();
    });
    setUsernameLogInError(null);
    setUsernameRegisterError(null);
    setIsLoading(true);
    let availableMethodsForUser = null;
    try {
      availableMethodsForUser = await props.client.getAvailableAuthenticationMethodsAsync(username);
    } catch (error) {
      if (!(error instanceof ApiError && error.statusCode === 404)) {
        if (currentAction() === "logIn") {
          setUsernameLogInError("unknown");
        } else {
          setUsernameRegisterError("unknown");
        }
        props.onError(error);
        return;
      }
    } finally {
      if (abortController.signal.aborted) return;
      setIsLoading(false);
    }
    switch (currentAction()) {
      case "logIn":
        {
          if (availableMethodsForUser === null) {
            setUsernameLogInError("userNotFound");
            props.onError(new UserNotFoundError());
            return;
          }
          break;
        }
      case "register":
        {
          if (availableMethodsForUser !== null) {
            setUsernameRegisterError("userAlreadyExists");
            props.onError(new UserAlreadyExistsError());
            return;
          }
          const availableMethods = {
            authenticator: false,
            magicLinkEmail: false,
            passkey: false,
            ...(props.interactiveConfig.permittedMethods ?? {
              authenticator: true,
              magicLinkEmail: props.interactiveConfig.defaultMagicLinkEmailRegisterRedirectUrl !== undefined,
              passkey: true
            })
          };
          if (availableMethods.magicLinkEmail && !/^[^@]+@[^@:/?#]*[^@:/.?#]+$/.test(username)) {
            availableMethods.magicLinkEmail = false;
          }
          availableMethodsForUser = availableMethods;
          break;
        }
    }
    const availableMethodsForUserList = Object.entries(availableMethodsForUser).filter(([_, isEnabled]) => isEnabled).map(([method]) => method);
    if (availableMethodsForUserList.length === 0 || currentMethod() !== null && !availableMethodsForUserList.includes(currentMethod())) {
      if (currentAction() === "logIn") {
        setUsernameLogInError("noAvailableMethods");
      } else {
        setUsernameRegisterError("noAvailableMethods");
      }
      props.onError(new NoAuthenticationMethodsAvailableError());
      return;
    }
    setAvailableMethods(availableMethodsForUser);
  }));
  createEffect(on([currentAction, currentUsername, currentMethod, isCaptchaPending, captchaConfirmation], async ([action, username, method, isCaptchaPending, captchaConfirmation]) => {
    if (action === null || username === null || method === null || isCaptchaPending) {
      return;
    }
    if (isCaptchaConfirmationStale()) {
      setIsCaptchaConfirmationStale(false);
      setCaptchaConfirmation(null);
      return;
    }
    if (captchaConfirmation !== null) {
      setIsCaptchaConfirmationStale(true);
    }
    const abortController = new AbortController();
    setIsLoading(true);
    setAuthenticatorError(null);
    setMagicLinkEmailError(null);
    setPasskeyError(null);
    setQrCodeData(null);
    onCleanup(() => {
      abortController.abort();
    });
    switch (method) {
      case "authenticator":
        {
          switch (action) {
            case "logIn":
              {
                let qrResult;
                try {
                  qrResult = await props.client.authenticateWithAuthenticatorAsync(username, {
                    ...props.interactiveConfig.defaultAuthenticateOptions,
                    ...props.interactiveConfig.defaultAuthenticatorAuthenticateOptions,
                    ...props.interactiveConfig.defaultAuthenticatorUserSpecificAuthenticateOptions
                  }, captchaConfirmation ?? undefined, abortController.signal);
                  if (abortController.signal.aborted) {
                    return;
                  }
                  setQrCodeData(qrResult.qrCodeUrl);
                } catch (error) {
                  if (abortController.signal.aborted) {
                    return;
                  }
                  if (error instanceof TypeError) {
                    setAuthenticatorError("network");
                  } else {
                    setAuthenticatorError("unknown");
                  }
                  props.onError(error);
                  return;
                } finally {
                  setIsLoading(false);
                }
                const authenticationResult = await qrResult.resultAsync();
                if (abortController.signal.aborted) {
                  return;
                }
                if (authenticationResult.succeeded) {
                  setIsSucceeded(true);
                  props.onLogIn(authenticationResult);
                } else {
                  setAuthenticatorError(authenticationResult.failureReason);
                  props.onLogInFailure(authenticationResult);
                }
                break;
              }
            case "register":
              {
                let qrResult;
                try {
                  qrResult = await props.client.registerWithAuthenticatorQrCodeAsync(username, {
                    ...props.interactiveConfig.defaultRegisterOptions,
                    ...props.interactiveConfig.defaultAuthenticatorRegisterOptions
                  }, abortController.signal);
                  if (abortController.signal.aborted) {
                    return;
                  }
                  setQrCodeData(qrResult.qrCodeUrl);
                } catch (error) {
                  if (abortController.signal.aborted) {
                    return;
                  }
                  if (error instanceof TypeError) {
                    setAuthenticatorError("network");
                  } else {
                    setAuthenticatorError("unknown");
                  }
                  props.onError(error);
                  return;
                } finally {
                  setIsLoading(false);
                }
                const registrationResult = await qrResult.resultAsync();
                if (abortController.signal.aborted) {
                  return;
                }
                if (registrationResult.succeeded) {
                  setIsSucceeded(true);
                  props.onRegister(registrationResult);
                } else {
                  setAuthenticatorError(registrationResult.failureReason);
                  props.onRegisterFailure(registrationResult);
                }
                break;
              }
          }
          break;
        }
      case "magicLinkEmail":
        {
          switch (currentAction()) {
            case "logIn":
              {
                try {
                  const authenticationResult = await props.client.sendAuthenticateMagicLinkEmailAsync(username, props.interactiveConfig.defaultMagicLinkEmailLogInRedirectUrl, {
                    ...props.interactiveConfig.defaultAuthenticateOptions,
                    ...props.interactiveConfig.defaultMagicLinkEmailAuthenticateOptions
                  }, captchaConfirmation ?? undefined);
                  if (abortController.signal.aborted) {
                    return;
                  }
                  if (authenticationResult.succeeded) {
                    setIsOutOfBandCompleted(true);
                    props.onOutOfBandLogIn(authenticationResult);
                  } else {
                    props.onLogInFailure(authenticationResult);
                  }
                } catch (error) {
                  if (abortController.signal.aborted) {
                    return;
                  }
                  if (error instanceof TypeError) {
                    setMagicLinkEmailError("network");
                  } else {
                    setMagicLinkEmailError("unknown");
                  }
                  props.onError(error);
                  return;
                } finally {
                  setIsLoading(false);
                }
                break;
              }
            case "register":
              {
                try {
                  const registrationResult = await props.client.sendRegisterMagicLinkEmailAsync(username, props.interactiveConfig.defaultMagicLinkEmailRegisterRedirectUrl, {
                    ...props.interactiveConfig.defaultRegisterOptions,
                    ...props.interactiveConfig.defaultMagicLinkEmailRegisterOptions
                  }, captchaConfirmation ?? undefined);
                  if (abortController.signal.aborted) {
                    return;
                  }
                  if (registrationResult.succeeded) {
                    setIsOutOfBandCompleted(true);
                    props.onOutOfBandRegister(registrationResult);
                  } else {
                    props.onRegisterFailure(registrationResult);
                  }
                } catch (error) {
                  if (abortController.signal.aborted) {
                    return;
                  }
                  if (error instanceof TypeError) {
                    setMagicLinkEmailError("network");
                  } else {
                    setMagicLinkEmailError("unknown");
                  }
                  props.onError(error);
                  return;
                } finally {
                  setIsLoading(false);
                }
                break;
              }
          }
          break;
        }
      case "passkey":
        {
          setIsLoading(false);
          switch (currentAction()) {
            case "logIn":
              {
                try {
                  const authenticationResult = await props.client.authenticateWithPasskeyAsync(username);
                  if (abortController.signal.aborted) {
                    return;
                  }
                  if (authenticationResult.succeeded) {
                    setIsSucceeded(true);
                    props.onLogIn(authenticationResult);
                  } else {
                    setPasskeyError(authenticationResult.failureReason);
                    props.onLogInFailure(authenticationResult);
                  }
                } catch (error) {
                  if (abortController.signal.aborted) {
                    return;
                  }
                  if (error instanceof TypeError) {
                    setPasskeyError("network");
                  } else {
                    setPasskeyError("unknown");
                  }
                  props.onError(error);
                  return;
                }
                break;
              }
            case "register":
              {
                try {
                  const registrationResult = await props.client.registerWithPasskeyAsync(username);
                  if (abortController.signal.aborted) {
                    return;
                  }
                  if (registrationResult.succeeded) {
                    setIsSucceeded(true);
                    props.onRegister(registrationResult);
                  } else {
                    setPasskeyError(registrationResult.failureReason);
                    props.onRegisterFailure(registrationResult);
                  }
                } catch (error) {
                  if (abortController.signal.aborted) {
                    return;
                  }
                  if (error instanceof TypeError) {
                    setPasskeyError("network");
                  } else {
                    setPasskeyError("unknown");
                  }
                  props.onError(error);
                  return;
                }
                break;
              }
          }
          break;
        }
    }
  }));
  const handleTabSelect = action => {
    if (isSucceeded()) return;
    setUserSelectedCurrentMethod(null);
    setUserSelectedCurrentUsername(null);
    setUserSelectedCurrentAction(action);
  };
  const handleUsernamelessRetry = () => {
    setUsernamelessLogInError(null);
  };
  const handleUsernameSelect = async username => {
    setUserSelectedCurrentUsername(username);
  };
  const handleMethodSelect = method => {
    setUserSelectedCurrentMethod(method);
  };
  const handleCaptchaConfirm = captchaConfirmation => {
    setIsCaptchaConfirmationStale(false);
    setCaptchaConfirmation(captchaConfirmation);
  };
  const goBackHandler = () => {
    if (isSucceeded()) {
      return undefined;
    } else if (userSelectedCurrentMethod() !== null) {
      return () => setUserSelectedCurrentMethod(null);
    } else if (userSelectedCurrentUsername() !== null) {
      return () => setUserSelectedCurrentUsername(null);
    } else {
      return undefined;
    }
  };
  return createComponent(TranslationTableContext.Provider, {
    value: tt,
    get children() {
      const _el$ = _tmpl$(),
        _el$2 = _el$.firstChild;
      insert(_el$, createComponent(Show, {
        get when() {
          return props.action === null;
        },
        get children() {
          return createComponent(TabControl, {
            get tabs() {
              return [{
                id: "logIn",
                displayName: tt().form.actions.logIn.tabName
              }, {
                id: "register",
                displayName: tt().form.actions.register.tabName
              }];
            },
            get activeTabId() {
              return currentAction();
            },
            onTabSelect: handleTabSelect
          });
        }
      }), _el$2);
      insert(_el$2, createComponent(Show, {
        get when() {
          return availableMethods() !== null || props.username !== null;
        },
        get fallback() {
          return createComponent(Switch, {
            get children() {
              return [createComponent(Match, {
                get when() {
                  return currentAction() === "logIn";
                },
                get children() {
                  return createComponent(LogInForm, {
                    get qrCodeData() {
                      return createMemo(() => !!enableUsernamelessLogIn())() ? usernamelessLogInQrCodeData() : false;
                    },
                    get verificationCode() {
                      return usernamelessLogInVerificationCode();
                    },
                    get isLoading() {
                      return isLoading();
                    },
                    get usernameError() {
                      return usernameLogInError();
                    },
                    get usernamelessError() {
                      return usernamelessLogInError();
                    },
                    onUsernamelessRetry: handleUsernamelessRetry,
                    onUsernameLogInRequest: handleUsernameSelect
                  });
                }
              }), createComponent(Match, {
                get when() {
                  return currentAction() === "register";
                },
                get children() {
                  return createComponent(RegisterForm, {
                    get isLoading() {
                      return isLoading();
                    },
                    get usernameError() {
                      return usernameRegisterError();
                    },
                    onUsernameRegisterRequest: handleUsernameSelect
                  });
                }
              })];
            }
          });
        },
        get children() {
          return createComponent(PromptContainer, {
            get username() {
              return currentUsername();
            },
            get isRegistering() {
              return currentAction() === "register";
            },
            get onGoBack() {
              return goBackHandler();
            },
            get children() {
              return createComponent(Show, {
                get when() {
                  return isSucceeded();
                },
                get fallback() {
                  return createComponent(Show, {
                    get when() {
                      return currentMethod() !== null;
                    },
                    get fallback() {
                      return createComponent(Show, {
                        get when() {
                          return availableMethods() !== null;
                        },
                        get fallback() {
                          return createComponent(WaitPrompt, {});
                        },
                        get children() {
                          return createComponent(MethodSelectionPrompt, {
                            get availableMethods() {
                              return availableMethods();
                            },
                            onSelect: handleMethodSelect
                          });
                        }
                      });
                    },
                    get children() {
                      return createComponent(CaptchaProtectedWindow, {
                        get hCaptchaSiteId() {
                          return hCaptchaSiteId() || null;
                        },
                        get isCaptchaRequired() {
                          return isCaptchaRequired();
                        },
                        onConfirm: handleCaptchaConfirm,
                        get children() {
                          return createComponent(Switch, {
                            get children() {
                              return [createComponent(Match, {
                                get when() {
                                  return currentMethod() === "authenticator";
                                },
                                get children() {
                                  return createComponent(AuthenticatorPrompt, {
                                    get qrCodeData() {
                                      return qrCodeData();
                                    },
                                    get isRegistering() {
                                      return currentAction() === "register";
                                    },
                                    get error() {
                                      return authenticatorError();
                                    },
                                    get onErrorRecoverRequest() {
                                      return goBackHandler();
                                    }
                                  });
                                }
                              }), createComponent(Match, {
                                get when() {
                                  return currentMethod() === "magicLinkEmail";
                                },
                                get children() {
                                  return createComponent(MagicLinkEmailPrompt, {
                                    get isRegistering() {
                                      return currentAction() === "register";
                                    },
                                    get isOutOfBandCompleted() {
                                      return isOutOfBandCompleted();
                                    },
                                    get error() {
                                      return magicLinkEmailError();
                                    },
                                    get onErrorRecoverRequest() {
                                      return goBackHandler();
                                    }
                                  });
                                }
                              }), createComponent(Match, {
                                get when() {
                                  return currentMethod() === "passkey";
                                },
                                get children() {
                                  return createComponent(PasskeyPrompt, {
                                    get isRegistering() {
                                      return currentAction() === "register";
                                    },
                                    get error() {
                                      return passkeyError();
                                    },
                                    get onErrorRecoverRequest() {
                                      return goBackHandler();
                                    }
                                  });
                                }
                              })];
                            }
                          });
                        }
                      });
                    }
                  });
                },
                get children() {
                  return createComponent(WaitPrompt, {});
                }
              });
            }
          });
        }
      }));
      createRenderEffect(_p$ => {
        const _v$ = styles.authArmorForm,
          _v$2 = uiOptionsDerivedStyles(),
          _v$3 = styles.view;
        _v$ !== _p$._v$ && className(_el$, _p$._v$ = _v$);
        _p$._v$2 = style(_el$, _v$2, _p$._v$2);
        _v$3 !== _p$._v$3 && className(_el$2, _p$._v$3 = _v$3);
        return _p$;
      }, {
        _v$: undefined,
        _v$2: undefined,
        _v$3: undefined
      });
      return _el$;
    }
  });
}

class LogInEvent extends Event {
  constructor(authenticationResult) {
    super("logIn");
    this.authenticationResult = authenticationResult;
  }
}

class RegisterEvent extends Event {
  constructor(registrationResult) {
    super("register");
    this.registrationResult = registrationResult;
  }
}

class OutOfBandLogInEvent extends Event {
  constructor(authenticationResult) {
    super("outOfBandLogIn");
    this.authenticationResult = authenticationResult;
  }
}

class OutOfBandRegisterEvent extends Event {
  constructor(registrationResult) {
    super("outOfBandRegister");
    this.registrationResult = registrationResult;
  }
}

class LogInFailureEvent extends Event {
  constructor(authenticationResult) {
    super("logInFailure");
    this.authenticationResult = authenticationResult;
  }
}

class RegisterFailureEvent extends Event {
  constructor(registrationResult) {
    super("registerFailure");
    this.registrationResult = registrationResult;
  }
}

class ErrorThrownEvent extends Event {
  constructor(error) {
    super("error");
    this.error = error;
  }
}

function registerAuthArmorForm() {
  customElement("autharmor-form", {
    client: null,
    interactiveConfig: {},
    action: null,
    username: null,
    method: null,
    defaultAction: null,
    enableUsernamelessLogIn: true
  }, (props, {
    element
  }) => {
    noShadowDOM();
    const handleLogIn = authenticationResult => {
      element.renderRoot.dispatchEvent(new LogInEvent(authenticationResult));
    };
    const handleRegister = registrationResult => {
      element.renderRoot.dispatchEvent(new RegisterEvent(registrationResult));
    };
    const handleOutOfBandLogIn = authenticationResult => {
      element.renderRoot.dispatchEvent(new OutOfBandLogInEvent(authenticationResult));
    };
    const handleOutOfBandRegister = registrationResult => {
      element.renderRoot.dispatchEvent(new OutOfBandRegisterEvent(registrationResult));
    };
    const handleLogInFailure = authenticationResult => {
      element.renderRoot.dispatchEvent(new LogInFailureEvent(authenticationResult));
    };
    const handleRegisterFailure = registrationResult => {
      element.renderRoot.dispatchEvent(new RegisterFailureEvent(registrationResult));
    };
    const handleError = error => {
      element.renderRoot.dispatchEvent(new ErrorThrownEvent(error));
    };
    return createComponent(Show, {
      get when() {
        return props.client instanceof AuthArmorClient;
      },
      get children() {
        return createComponent(AuthArmorForm, mergeProps(props, {
          onLogIn: handleLogIn,
          onRegister: handleRegister,
          onOutOfBandLogIn: handleOutOfBandLogIn,
          onOutOfBandRegister: handleOutOfBandRegister,
          onLogInFailure: handleLogInFailure,
          onRegisterFailure: handleRegisterFailure,
          onError: handleError
        }));
      }
    });
  });
}

registerAuthArmorForm();
